import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import EpsfPageBreadcrumb from "../EpsfPageBreadcrumb";
import ReportTable from "../../../common/ReportTable/ReportTable";
import Dropdown from "../../../common/dropDown";
import SegmentDatePicker from "../../../common/aqPickers/SegmentDatePicker";
import SegmentTimePicker from "../../../common/aqPickers/SegmentTimePicker";
import InfoAlert from "../../../common/InfoAlert";
import useInfoAlert from "../../../../hooks/useInfoAlert";
import { openAuthModal } from "../../../common/authmodal/authmodalservice";
import { openESignatureConfirmModal } from "../../../common/esignature/esignatureConfirmService";
import { getUserData } from "../../../../services/apiService";
import { API_CONFIG } from "../../../../config/apiconfig";
import { EpsfAccessGate, useEpsfAccess } from "../epsfAccess";
import {
  findOption,
  formatTxnWhen,
  getImpTxnRowKey,
  IMP_RECEIPT_DEFAULT_COLUMNS,
  IMP_RECEIPT_FIELD_OPTIONS,
  renderImpProductCell,
  renderImpTxnCell,
  toApiDateString,
  toApiDateTimeString,
  useEpsfLedger,
  useEpsfStudySite,
} from "../epsfLedgerUi";
import {
  formatReviewReasonLabel,
  renderConditionBadge,
  renderReceiptStatusBadge,
} from "../epsfBadges";
import {
  formatAuditFieldLabelDisplay,
  formatAuditReasonDisplay,
  renderAuditActor,
  renderAuditNewValue,
  exportAuditLogToExcel,
} from "../epsfAuditDisplay";
import utils from "../../../../utils/utils";
import {
  formatStorageTempRange,
  formatStrengthDisplay,
} from "./impProductExcel";
import {
  formatTempRange,
  isStorageCompatibleWithImp,
  receiptTempStatus,
} from "../epsfTempCompat";
import {
  ensureFormOccurrence,
  fetchFormWorkItems,
  fetchImpProducts,
  fetchReceiptDetail,
  fetchReceiveModes,
  fetchStorageUnits,
  linkFormInstance,
  postImpReceipt,
  uploadReceiptEvidence,
  validateKitShipment,
  createReceiveScanSession,
  pollReceiveScanEvents,
  closeReceiveScanSession,
  fetchReceivableBatches,
  fetchStudyImpKitMaps,
} from "../epsfService";
import { createInstance } from "../../esource/esourceService";
import EpsfFormFillModal from "../EpsfFormFillModal";
import {
  getSessionRoleName,
  isSessionSuperAdmin,
  isSystemAdministratorRole,
} from "../../../../utils/permissionUtils";
import ReviewHeldStockModal from "./inventory/ReviewHeldStockModal";
import { parseEpsfBarcodePayload } from "./epsfBarcodeParse";
import "../EpsfPages.scss";

function parseIsoDateLocal(value) {
  const text = String(value || "").trim().slice(0, 10);
  const parts = text.split("-");
  if (parts.length !== 3) return null;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return new Date(y, m - 1, d);
}

function parseKitsJson(raw) {
  if (Array.isArray(raw)) {
    return raw
      .map((r) => String(r?.kitNumber || r?.KitNumber || r || "").trim())
      .filter(Boolean);
  }
  const text = String(raw || "").trim();
  if (!text) return [];
  try {
    const parsed = JSON.parse(text);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((r) => String(r?.kitNumber || r?.KitNumber || r || "").trim())
      .filter(Boolean);
  } catch {
    return [];
  }
}

function receivableBatchKey(batchNumber, expiryIso) {
  return `${String(batchNumber || "").trim()}|||${String(expiryIso || "").trim()}`;
}

function toDateInputValue(raw) {
  if (raw == null || raw === "") return "";
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    const y = raw.getFullYear();
    const m = String(raw.getMonth() + 1).padStart(2, "0");
    const d = String(raw.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const text = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return "";
  return toDateInputValue(parsed);
}

/** Days ahead that count as near expiry for batch chips. */
const NEAR_EXPIRY_DAYS = 30;

/** @returns {'expired'|'near'|'ok'|'none'} */
function expiryToneOf(expiryIso) {
  const iso = toDateInputValue(expiryIso);
  if (!iso) return "none";
  const exp = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(exp.getTime())) return "none";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((exp.getTime() - today.getTime()) / 86400000);
  if (diffDays < 0) return "expired";
  if (diffDays <= NEAR_EXPIRY_DAYS) return "near";
  return "ok";
}

function expiryChipLabel(expiryIso) {
  const iso = toDateInputValue(expiryIso);
  if (!iso) return "No expiry";
  if (expiryToneOf(iso) === "expired") return `Expired ${iso}`;
  return `Exp ${iso}`;
}

function formatKitExpiryDisplay(dateOrIso) {
  const iso = toApiDateString(dateOrIso) || toDateInputValue(dateOrIso);
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d} / ${m} / ${y}`;
}

/** @returns {'mapped'|'partial'|'none'} */
function mappingToneOf(mappedCount, kitCount) {
  const mapped = Number(mappedCount) || 0;
  const total = Number(kitCount) || 0;
  if (total <= 0 || mapped <= 0) return "none";
  if (mapped >= total) return "mapped";
  return "partial";
}

function kitImpProductLabel(row) {
  const neutral = String(
    row?.NeutralDisplayName || row?.neutralDisplayName || "",
  ).trim();
  if (neutral) return neutral;
  const code = String(row?.ProductCode || row?.productCode || "").trim();
  const name = String(row?.ProductName || row?.productName || "").trim();
  if (code && name) return `${code} · ${name}`;
  if (name) return name;
  if (code) return code;
  const treatment = String(
    row?.TreatmentType || row?.treatmentType || "",
  ).trim();
  return treatment || "Mapped IMP";
}

/**
 * Prepared batches for Add Receipt: IMP-mapped kits that are not yet received.
 * Same chip UI as ImpSponsorSetup kit mapping.
 */
function buildPreparedBatchOptions(kitMaps, receivableByKey = new Map()) {
  const byKey = new Map();
  (kitMaps || []).forEach((row) => {
    const batchNumber = String(
      row?.BatchNumber || row?.batchNumber || "",
    ).trim();
    if (!batchNumber) return;

    const mappingStatus = String(
      row?.MappingStatus || row?.mappingStatus || "",
    );
    const needsResolution = mappingStatus === "RequiresSponsorResolution";
    const studyImpProductId = String(
      row?.StudyImpProductId || row?.studyImpProductId || "",
    ).trim();
    const isImpMapped = Boolean(studyImpProductId) && !needsResolution;
    if (!isImpMapped) return;

    const physicalStatus = String(
      row?.PhysicalStatus || row?.physicalStatus || "",
    ).trim();
    const impKitId = String(row?.ImpKitId || row?.impKitId || "").trim();
    const notReceived =
      !impKitId ||
      physicalStatus.toLowerCase() === "notreceived" ||
      physicalStatus.toLowerCase() === "not received";
    if (!notReceived) return;

    const expiryIso = toDateInputValue(
      row?.ExpiryDate || row?.expiryDate || "",
    );
    const key = receivableBatchKey(batchNumber, expiryIso);
    const kitNumber = String(row?.KitNumber || row?.kitNumber || "").trim();
    if (!kitNumber) return;

    const detail = {
      kitNumber,
      expiryIso,
      studyImpProductId,
      impProductId: String(row?.ImpProductId || row?.impProductId || "").trim(),
      productLabel: kitImpProductLabel(row),
      treatmentType: String(
        row?.TreatmentType || row?.treatmentType || "",
      ).trim(),
    };

    if (byKey.has(key)) {
      const cur = byKey.get(key);
      cur.kitCount += 1;
      cur.mappedCount += 1;
      if (!cur.kitSet.has(kitNumber.toUpperCase())) {
        cur.kitSet.add(kitNumber.toUpperCase());
        cur.kits.push(kitNumber);
        cur.kitDetails.push(detail);
      }
      return;
    }
    byKey.set(key, {
      value: key,
      batchNumber,
      expiryIso,
      kitCount: 1,
      mappedCount: 1,
      kitSet: new Set([kitNumber.toUpperCase()]),
      kits: [kitNumber],
      kitDetails: [detail],
    });
  });

  return [...byKey.values()]
    .map((opt) => {
      const receivable = receivableByKey.get(opt.value);
      const mappedSet = new Set(
        (opt.kits || []).map((k) => String(k).toUpperCase()),
      );
      let kitDetails = opt.kitDetails || [];
      if (Array.isArray(receivable?.kits) && receivable.kits.length) {
        const recvSet = new Set(
          receivable.kits.map((k) => String(k).toUpperCase()),
        );
        const filtered = kitDetails.filter((d) =>
          recvSet.has(String(d.kitNumber).toUpperCase()),
        );
        if (filtered.length) kitDetails = filtered;
      } else {
        kitDetails = kitDetails.filter((d) =>
          mappedSet.has(String(d.kitNumber).toUpperCase()),
        );
      }
      const kitsForReceive = kitDetails.map((d) => d.kitNumber);
      return {
        value: opt.value,
        batchNumber: opt.batchNumber,
        expiryIso: opt.expiryIso,
        expiryTone: expiryToneOf(opt.expiryIso),
        kitCount: opt.kitCount,
        mappedCount: opt.mappedCount,
        mappingTone: mappingToneOf(opt.mappedCount, opt.kitCount),
        kits: kitsForReceive,
        kitDetails,
        allKits: opt.kits,
        unreceivedCount: kitsForReceive.length,
        label: `${opt.batchNumber}${
          opt.expiryIso ? ` · exp ${opt.expiryIso}` : ""
        } · ${opt.mappedCount}/${opt.kitCount} Mapped`,
      };
    })
    .filter((opt) => (opt.kits || []).length > 0)
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
}

function indexReceivableBatches(rows) {
  const map = new Map();
  (rows || []).forEach((row) => {
    const batchNumber = String(
      row?.BatchNumber || row?.batchNumber || "",
    ).trim();
    if (!batchNumber) return;
    const expiryIso = toDateInputValue(
      row?.ExpiryDate || row?.expiryDate || "",
    );
    const key = receivableBatchKey(batchNumber, expiryIso);
    map.set(key, {
      kits: parseKitsJson(row?.KitsJson || row?.kitsJson),
    });
  });
  return map;
}

/** Fallback list when kit maps are not available (site receive) — same chip UI. */
function buildReceivableOnlyBatchOptions(rows) {
  return (rows || [])
    .map((row) => {
      const batchNumber = String(
        row?.BatchNumber || row?.batchNumber || "",
      ).trim();
      if (!batchNumber) return null;
      const expiryIso = toDateInputValue(
        row?.ExpiryDate || row?.expiryDate || "",
      );
      const kits = parseKitsJson(row?.KitsJson || row?.kitsJson);
      const kitCount =
        Number(row?.KitCount ?? row?.kitCount ?? kits.length) || kits.length;
      return {
        value: receivableBatchKey(batchNumber, expiryIso),
        batchNumber,
        expiryIso,
        expiryTone: expiryToneOf(expiryIso),
        kitCount,
        mappedCount: kitCount,
        mappingTone: mappingToneOf(kitCount, kitCount),
        kits,
        allKits: kits,
        unreceivedCount: kits.length,
        label: `${batchNumber}${
          expiryIso ? ` · exp ${expiryIso}` : ""
        } · ${kitCount}/${kitCount} Mapped`,
      };
    })
    .filter(Boolean)
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
}

function normalizeGuid(value) {
  return String(value || "")
    .replace(/[{}]/g, "")
    .trim()
    .toLowerCase();
}

function userCanFillFormWorkItem(userData, workItem) {
  if (!workItem) return false;
  const workType = String(
    workItem.WorkType || workItem.workType || "COMPLETE",
  ).toUpperCase();
  if (workType === "REVIEW") return false;

  const sessionRole = getSessionRoleName(userData);
  if (
    isSystemAdministratorRole(sessionRole) ||
    isSessionSuperAdmin(userData)
  ) {
    return true;
  }

  const requiredRoleId = normalizeGuid(
    workItem.ResponsibleRoleId ||
      workItem.responsibleRoleId ||
      workItem.CompletedByRoleId ||
      workItem.completedByRoleId,
  );
  const userRoleId = normalizeGuid(
    userData?.RoleId || userData?.roleId || userData?.RolesId,
  );
  if (requiredRoleId && userRoleId && requiredRoleId === userRoleId) {
    return true;
  }

  const requiredRole = String(
    workItem.ResponsibleRole ||
      workItem.responsibleRole ||
      workItem.AssigneeRole ||
      workItem.assigneeRole ||
      "",
  )
    .trim()
    .toLowerCase();
  if (!requiredRole) return false;
  return sessionRole.trim().toLowerCase() === requiredRole;
}

function userCanReviewFormWorkItem(userData, workItem) {
  if (!workItem) return false;
  const workType = String(
    workItem.WorkType || workItem.workType || "",
  ).toUpperCase();
  if (workType !== "REVIEW") return false;

  const sessionRole = getSessionRoleName(userData);
  if (
    isSystemAdministratorRole(sessionRole) ||
    isSessionSuperAdmin(userData)
  ) {
    return true;
  }

  const requiredRoleId = normalizeGuid(
    workItem.ResponsibleRoleId || workItem.responsibleRoleId,
  );
  const userRoleId = normalizeGuid(
    userData?.RoleId || userData?.roleId || userData?.RolesId,
  );
  if (requiredRoleId && userRoleId && requiredRoleId === userRoleId) {
    return true;
  }

  const requiredRole = String(
    workItem.ResponsibleRole || workItem.responsibleRole || "",
  )
    .trim()
    .toLowerCase();
  if (!requiredRole) return false;
  return sessionRole.trim().toLowerCase() === requiredRole;
}

function isReceiptFormTrigger(trigger) {
  const t = String(trigger || "").toUpperCase();
  return !t || t === "ONRECEIPT" || t === "RECEIPT_POSTED";
}

function workItemStatus(row) {
  return String(
    row?.WorkItemStatus || row?.workItemStatus || row?.Status || row?.status || "",
  ).toUpperCase();
}

function workItemType(row) {
  return String(row?.WorkType || row?.workType || "COMPLETE").toUpperCase();
}

function workflowStatus(row) {
  return String(
    row?.WorkflowStatus || row?.workflowStatus || "",
  ).toUpperCase();
}

/** Pick fill / approve / locked phase for a receipt's form work items. */
function summarizeReceiptFormItems(items = []) {
  const list = Array.isArray(items) ? items : [];
  const openFill = list.filter((row) => {
    const st = workItemStatus(row);
    const wt = workItemType(row);
    return st === "OPEN" && (wt === "COMPLETE" || wt === "CORRECT");
  });
  if (openFill.length) {
    return { phase: "fill", items: openFill, label: "Fill form" };
  }

  const openReview = list.filter((row) => {
    const st = workItemStatus(row);
    const wt = workItemType(row);
    return st === "OPEN" && wt === "REVIEW";
  });
  if (openReview.length) {
    return {
      phase: "approve",
      items: openReview,
      label: "Approval pending",
    };
  }

  const locked = list.filter((row) => {
    const wf = workflowStatus(row);
    const occ = String(
      row?.OccurrenceStatus || row?.occurrenceStatus || "",
    ).toUpperCase();
    return (
      wf === "APPROVED" ||
      wf === "COMPLETED" ||
      occ === "COMPLETE" ||
      occ === "COMPLETED"
    );
  });
  if (locked.length) {
    const wf = workflowStatus(locked[0]);
    return {
      phase: "locked",
      items: [locked[0]],
      label: wf === "APPROVED" ? "Approved" : "Completed",
    };
  }

  return { phase: "none", items: [], label: "" };
}

const EVIDENCE_TYPE_OPTIONS = [
  { value: "DeliveryNote", label: "Delivery Note" },
  { value: "PackingList", label: "Packing List" },
  { value: "TransitTemperatureLogger", label: "Transit Temperature Logger" },
  { value: "BatchCertificate", label: "Batch Certificate / CoA" },
  { value: "IrtAcknowledgement", label: "IRT Acknowledgement" },
  { value: "SponsorDepotDocument", label: "Sponsor/Depot Document" },
  { value: "Other", label: "Other" },
];

const DETAIL_TABS = [
  { id: "overview", label: "Overview" },
  { id: "kits", label: "Kits" },
  { id: "documents", label: "Documents" },
  { id: "audit", label: "Audit" },
];

const CONDITION_OPTIONS = [
  { value: "", label: "Select condition" },
  { value: "Acceptable", label: "Acceptable" },
  { value: "Damaged", label: "Damaged" },
  { value: "TemperatureConcern", label: "Temperature concern" },
  { value: "PackagingIssue", label: "Packaging issue" },
  { value: "QuantityDiscrepancy", label: "Quantity discrepancy" },
  { value: "Other", label: "Other" },
];

const STORAGE_OVERRIDE_REASON_OPTIONS = [
  { value: "", label: "Select override reason" },
  { value: "TemporaryStorage", label: "Temporary storage" },
  { value: "PrimaryUnavailable", label: "Primary storage unavailable" },
  { value: "QuarantineHold", label: "Quarantine / hold" },
  { value: "TempExcursionInvestigation", label: "Temperature excursion investigation" },
  { value: "AwaitingTransfer", label: "Awaiting transfer" },
  { value: "SponsorInstruction", label: "Sponsor instruction" },
  { value: "Emergency", label: "Emergency situation" },
  { value: "Other", label: "Other" },
];

function formatExpiryDisplay(value) {
  if (!value) return "";
  const text = String(value).slice(0, 10);
  const parts = text.split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return text;
}

function overrideReasonLabel(code) {
  const match = STORAGE_OVERRIDE_REASON_OPTIONS.find(
    (o) => o.value && o.value === code,
  );
  return match?.label || formatReviewReasonLabel(code);
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function nowTimeHHmm() {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function staffDisplayName(user) {
  if (!user) return "";
  const name = [
    user.FirstName || user.firstName,
    user.LastName || user.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (name) return name;
  return String(
    user.StaffName ||
      user.staffName ||
      user.FullName ||
      user.fullName ||
      user.UserName ||
      user.userName ||
      "",
  ).trim();
}

function staffEmail(user) {
  if (!user) return "";
  return String(user.EmailId || user.emailId || "").trim();
}

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

const emptyForm = (staffId = "", staffName = "", staffEmailAddr = "") => ({
  receiveMode: "IndividualKit",
  impProductId: "",
  batchNumber: "",
  expiryDate: null,
  quantity: "",
  kitNumbersText: "",
  /** Per-kit custody fields keyed by kit number (upper). */
  kitLineFields: {},
  defaultKitBatchNumber: "",
  defaultKitExpiryDate: null,
  referenceNo: "",
  receivedDate: new Date(),
  receivedTime: nowTimeHHmm(),
  receivedByStaffId: staffId,
  receivedByName: staffName,
  receivedByEmail: staffEmailAddr,
  conditionCode: "",
  conditionDetails: "",
  storageUnitId: "",
  storageRackId: "",
  allowStorageOverride: false,
  storageOverrideReason: "",
  storageOverrideJustification: "",
  temperatureOnReceipt: "",
  temperatureWhenReceived: "",
  notes: "",
});

/** Unique kit numbers from paste (newline / comma / semicolon / tab). */
function parseKitNumbers(text) {
  return parseKitNumbersDetailed(text).kits;
}

/** Unique kits + duplicate kit strings found in the raw paste. */
function parseKitNumbersDetailed(text) {
  const seen = new Set();
  const kits = [];
  const duplicates = [];
  String(text || "")
    .split(/[\n,;\t]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((kit) => {
      const key = kit.toUpperCase();
      if (seen.has(key)) {
        if (!duplicates.some((d) => d.toUpperCase() === key)) duplicates.push(kit);
        return;
      }
      seen.add(key);
      kits.push(kit);
    });
  return { kits, duplicates };
}

function joinKitNumbers(kits) {
  const seen = new Set();
  const out = [];
  (Array.isArray(kits) ? kits : []).forEach((kit) => {
    const text = String(kit || "").trim();
    if (!text) return;
    const key = text.toUpperCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(text);
  });
  return out.join("\n");
}

const KIT_RANGE_MAX = 2000;

/**
 * Build kit numbers from prefix + numeric from/to (zero-padding taken from From).
 * Example: prefix "KIT-", from "001", to "050" → KIT-001 … KIT-050
 */
function generateKitNumberSeries(prefix, fromRaw, toRaw) {
  const fromStr = String(fromRaw ?? "").trim();
  const toStr = String(toRaw ?? "").trim();
  if (!/^\d+$/.test(fromStr) || !/^\d+$/.test(toStr)) {
    return { ok: false, message: "From and To must be whole numbers." };
  }
  const from = Number(fromStr);
  const to = Number(toStr);
  if (!Number.isFinite(from) || !Number.isFinite(to)) {
    return { ok: false, message: "From and To must be whole numbers." };
  }
  if (to < from) {
    return { ok: false, message: "To must be greater than or equal to From." };
  }
  const count = to - from + 1;
  if (count > KIT_RANGE_MAX) {
    return {
      ok: false,
      message: `Series is limited to ${KIT_RANGE_MAX} kits at a time.`,
    };
  }
  const padWidth = Math.max(fromStr.length, toStr.length);
  const pre = String(prefix ?? "");
  const kits = [];
  for (let n = from; n <= to; n += 1) {
    kits.push(`${pre}${String(n).padStart(padWidth, "0")}`);
  }
  const tooLong = kits.find((k) => k.length > 100);
  if (tooLong) {
    return {
      ok: false,
      message: `Generated kit number exceeds 100 characters: ${tooLong}`,
    };
  }
  return { ok: true, kits, count };
}

/** Show “Scan with phone” companion (QR / link) on Receive. */
const ENABLE_PHONE_REMOTE_SCAN = true;

/** Table entry for Individual Kit receive — one kit number per row. */
/** Table entry for Individual Kit receive — Kit number, Expiry, Remove. */
function KitNumbersTableInput({
  value = "",
  onChange,
  disabled = false,
  invalid = false,
  errorMessage = "",
  placeholder = "Enter kit number",
  onStructuredFields = null,
  autoFocus = false,
  getExpiryForKit = null,
  onExpiryChange = null,
  defaultExpiry = null,
}) {
  const inputRef = useRef(null);
  const [draft, setDraft] = useState("");
  const [draftExpiry, setDraftExpiry] = useState(defaultExpiry || null);
  const [scanHint, setScanHint] = useState("");
  const [rowDrafts, setRowDrafts] = useState({});
  const kits = useMemo(() => parseKitNumbers(value), [value]);

  useEffect(() => {
    setDraftExpiry(defaultExpiry || null);
  }, [defaultExpiry]);

  useEffect(() => {
    setRowDrafts((prev) => {
      const next = {};
      kits.forEach((kit) => {
        const key = kit.toUpperCase();
        next[key] = Object.prototype.hasOwnProperty.call(prev, key)
          ? prev[key]
          : kit;
      });
      return next;
    });
  }, [kits]);

  useEffect(() => {
    if (autoFocus && !disabled) {
      const t = window.setTimeout(() => inputRef.current?.focus(), 50);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [autoFocus, disabled, kits.length]);

  const mergeKits = (incoming, expiryOverride = undefined) => {
    const incomingList = Array.isArray(incoming) ? incoming : [];
    const existing = new Set(kits.map((k) => k.toUpperCase()));
    const dupes = [];
    const fresh = [];
    incomingList.forEach((kit) => {
      const text = String(kit || "").trim();
      const key = text.toUpperCase();
      if (!key) return;
      if (existing.has(key) || fresh.some((f) => f.toUpperCase() === key)) {
        dupes.push(text);
        return;
      }
      existing.add(key);
      fresh.push(text);
    });
    if (fresh.length) {
      onChange?.(joinKitNumbers([...kits, ...fresh]));
      const expiry =
        expiryOverride !== undefined
          ? expiryOverride
          : draftExpiry || defaultExpiry || null;
      if (typeof onExpiryChange === "function" && expiry) {
        fresh.forEach((kit) => onExpiryChange(kit, expiry));
      }
    }
    return { added: fresh.length, duplicates: dupes };
  };

  const commitParsedScan = (raw) => {
    if (disabled) return;
    const parsed = parseEpsfBarcodePayload(raw);
    if (!parsed.kitNumber) {
      setScanHint("Could not read a Kit Number from the scan.");
      window.setTimeout(() => inputRef.current?.focus(), 0);
      return;
    }
    if (parsed.batchNumber || parsed.expiryDate) {
      onStructuredFields?.({
        kitNumber: parsed.kitNumber,
        batchNumber: parsed.batchNumber,
        expiryDate: parsed.expiryDate,
      });
    }
    const expiryForKit = parsed.expiryDate || draftExpiry || defaultExpiry || null;
    const result = mergeKits([parsed.kitNumber], expiryForKit);
    if (result.duplicates.length) {
      setScanHint(`Kit ${parsed.kitNumber} has already been added.`);
    } else {
      setScanHint("");
    }
    setDraft("");
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commitDraft = () => {
    if (!draft.trim() || disabled) return;
    commitParsedScan(draft);
  };

  const removeKit = (kit) => {
    if (disabled) return;
    const key = String(kit || "").toUpperCase();
    setRowDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    onChange?.(
      joinKitNumbers(kits.filter((k) => k.toUpperCase() !== key)),
    );
  };

  const commitRowKit = (originalKit) => {
    if (disabled) return;
    const oldKey = String(originalKit || "").toUpperCase();
    const nextName = String(rowDrafts[oldKey] ?? originalKit).trim();
    if (!nextName) {
      setRowDrafts((prev) => ({ ...prev, [oldKey]: originalKit }));
      setScanHint("Kit number cannot be empty.");
      return;
    }
    if (nextName.length > 100) {
      setRowDrafts((prev) => ({ ...prev, [oldKey]: originalKit }));
      setScanHint("Kit number cannot exceed 100 characters.");
      return;
    }
    const newKey = nextName.toUpperCase();
    if (newKey !== oldKey) {
      const duplicate = kits.some(
        (k) => k.toUpperCase() === newKey && k.toUpperCase() !== oldKey,
      );
      if (duplicate) {
        setRowDrafts((prev) => ({ ...prev, [oldKey]: originalKit }));
        setScanHint(`Kit ${nextName} has already been added.`);
        return;
      }
    }
    if (nextName === originalKit) {
      setScanHint("");
      return;
    }
    onChange?.(
      joinKitNumbers(
        kits.map((k) => (k.toUpperCase() === oldKey ? nextName : k)),
      ),
    );
    if (typeof onExpiryChange === "function" && newKey !== oldKey) {
      const expiry = resolveExpiry(originalKit);
      if (expiry) onExpiryChange(nextName, expiry);
    }
    setRowDrafts((prev) => {
      const next = { ...prev };
      delete next[oldKey];
      next[newKey] = nextName;
      return next;
    });
    setScanHint("");
  };

  const resolveExpiry = (kit) => {
    if (typeof getExpiryForKit === "function") {
      return getExpiryForKit(kit) || null;
    }
    return defaultExpiry || null;
  };

  return (
    <div className={`epsf-kit-table-input${invalid ? " is-invalid" : ""}`}>
      {scanHint ? (
        <div className="epsf-kit-table-input__hint" role="status">
          {scanHint}
        </div>
      ) : null}

      {!disabled ? (
        <div className="epsf-kit-table-input__add-bar">
          <input
            ref={inputRef}
            className={`form-control epsf-kit-table-input__control${
              invalid && !kits.length ? " is-invalid" : ""
            }`}
            value={draft}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            aria-label="Add kit number"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === "," || e.key === ";") {
                e.preventDefault();
                e.stopPropagation();
                commitDraft();
              }
            }}
            onPaste={(e) => {
              const text = e.clipboardData?.getData("text");
              if (!text || !/[\n,;\t]/.test(text)) return;
              e.preventDefault();
              const result = mergeKits([
                ...parseKitNumbers(text),
                ...parseKitNumbers(draft),
              ]);
              if (result.duplicates.length) {
                setScanHint(
                  `Kit ${result.duplicates[0]} has already been added.`,
                );
              } else {
                setScanHint("");
              }
              setDraft("");
            }}
          />
          <div className="epsf-kit-table-input__add-expiry">
            <SegmentDatePicker
              selected={draftExpiry}
              onChange={(date) => setDraftExpiry(date)}
              isClearable
              disabled={disabled}
              aria-label="Expiry for new kit"
            />
          </div>
          <button
            type="button"
            className="btn btn-outline-primary epsf-kit-table-input__add-btn"
            disabled={disabled || !draft.trim()}
            onClick={commitDraft}
          >
            Add
          </button>
        </div>
      ) : null}

      {errorMessage || (invalid && !kits.length) ? (
        <div className="epsf-form-field-error epsf-kit-table-input__error" role="alert">
          {errorMessage || "Enter at least one kit number."}
        </div>
      ) : null}

      <div className="epsf-kit-table-input__divider" aria-hidden="true" />

      <div className="epsf-kit-table-input__cols" aria-hidden={kits.length === 0}>
        <span>Kit number</span>
        <span>Expiry</span>
        <span />
      </div>

      <div className="epsf-kit-table-input__list" role="list">
        {kits.length ? (
          kits.map((kit) => {
            const key = kit.toUpperCase();
            return (
              <div
                key={key}
                className="epsf-kit-table-input__row"
                role="listitem"
              >
                <div className="epsf-kit-table-input__kit">
                  <input
                    className="form-control epsf-kit-table-input__control"
                    value={rowDrafts[key] ?? kit}
                    disabled={disabled}
                    maxLength={100}
                    aria-label={`Kit number ${kit}`}
                    onChange={(e) => {
                      const nextVal = e.target.value;
                      setRowDrafts((prev) => ({ ...prev, [key]: nextVal }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.currentTarget.blur();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        setRowDrafts((prev) => ({ ...prev, [key]: kit }));
                        e.currentTarget.blur();
                      }
                    }}
                    onBlur={() => commitRowKit(kit)}
                  />
                </div>
                <div className="epsf-kit-table-input__expiry">
                  <SegmentDatePicker
                    selected={resolveExpiry(kit)}
                    onChange={(date) => onExpiryChange?.(kit, date)}
                    isClearable
                    disabled={disabled || typeof onExpiryChange !== "function"}
                    aria-label={`Expiry ${kit}`}
                  />
                </div>
                <div className="epsf-kit-table-input__actions">
                  {!disabled ? (
                    <button
                      type="button"
                      className="btn btn-outline-secondary epsf-kit-table-input__remove-btn"
                      onClick={() => removeKit(kit)}
                      aria-label={`Remove ${kit}`}
                      title="Remove"
                    >
                      <i className="fas fa-trash-alt" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })
        ) : (
          <div className="epsf-kit-table-input__empty">
            No kits added yet. Type a kit number above and press Enter or Add.
          </div>
        )}
      </div>
    </div>
  );
}
function newClientReceiptId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export default function ImpReceipt({
  overlay = false,
  preferImpProductId = null,
  onOverlayClose = null,
  onOverlaySuccess = null,
} = {}) {
  const { loading, canView, moduleAccess, canReceive } = useEpsfAccess();
  const allow = canView && moduleAccess;
  const navigate = useNavigate();
  const ctx = useEpsfStudySite();
  const { txns, error, setError, refresh, loading: listLoading } = useEpsfLedger(
    ctx.studyId,
    ctx.siteId,
    "Receipt",
  );
  const [formDueByTxn, setFormDueByTxn] = useState({});
  const [formFillBusy, setFormFillBusy] = useState(false);
  const [formFillModal, setFormFillModal] = useState(null);
  const [formDueTick, setFormDueTick] = useState(0);
  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const refreshFormDues = useCallback(() => {
    setFormDueTick((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ctx.studyId || !ctx.siteId) {
        setFormDueByTxn({});
        return;
      }
      try {
        /* Include completed + review work items so FORMS can show
           Fill form / Approval pending / Approved after submit. */
        const work = await fetchFormWorkItems(ctx.studyId, ctx.siteId, "*");
        if (cancelled) return;
        const map = {};
        (work.rows || []).forEach((row) => {
          const txnId = String(
            row.LinkedTxnId || row.linkedTxnId || "",
          ).toLowerCase();
          if (!txnId || !isReceiptFormTrigger(row.TriggerCode || row.triggerCode)) {
            return;
          }
          if (!map[txnId]) map[txnId] = [];
          map[txnId].push(row);
        });
        setFormDueByTxn(map);
      } catch {
        if (!cancelled) setFormDueByTxn({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ctx.studyId, ctx.siteId, txns, formDueTick]);

  const receiptRows = useMemo(
    () =>
      (txns || []).map((row) => {
        const txnId = String(row.ImpTxnId || row.impTxnId || "").toLowerCase();
        const dues = formDueByTxn[txnId] || [];
        const summary = summarizeReceiptFormItems(dues);
        const titles = summary.items.map(
          (d) =>
            d.Title ||
            d.title ||
            d.TemplateName ||
            d.templateName ||
            summary.label ||
            "Form",
        );
        return {
          ...row,
          FormDue: titles.length ? titles.join("; ") : "",
          FormDueCount: summary.items.length,
          FormDueItems: summary.items,
          FormDuePhase: summary.phase,
          FormDueLabel: summary.label,
        };
      }),
    [txns, formDueByTxn],
  );

  const openReceiptFormFill = useCallback(
    async (row) => {
      const phase = row?.FormDuePhase || "fill";
      const items = row?.FormDueItems || [];
      const workItem = items[0];
      if (!workItem) return;

      const user = getUserData() || {};
      if (phase === "fill" || phase === "none") {
        if (!userCanFillFormWorkItem(user, workItem)) {
          showAlert({
            title: "Not authorised",
            message: "You are not authorized to fill this form",
            type: "info",
          });
          return;
        }
      } else if (phase === "approve") {
        if (!userCanReviewFormWorkItem(user, workItem)) {
          /* Still allow read-only view of submitted answers */
          setFormFillBusy(true);
          try {
            const instanceId =
              workItem.FormInstanceId || workItem.formInstanceId || null;
            if (!instanceId) {
              showAlert({
                title: "Form unavailable",
                message: "No submitted form instance is linked for review.",
                type: "info",
              });
              return;
            }
            setFormFillModal({
              formInstanceId: instanceId,
              workItem: {
                ...workItem,
                FormInstanceId: instanceId,
                LinkedTxnId:
                  workItem.LinkedTxnId ||
                  workItem.linkedTxnId ||
                  row?.ImpTxnId ||
                  row?.impTxnId ||
                  null,
                StudyId: workItem.StudyId || ctx.studyId,
                SiteId: workItem.SiteId || ctx.siteId,
              },
              mode: "pending",
            });
          } finally {
            setFormFillBusy(false);
          }
          return;
        }
      }

      setFormFillBusy(true);
      try {
        let instanceId =
          workItem.FormInstanceId || workItem.formInstanceId || null;
        const occurrenceId =
          workItem.FormOccurrenceId || workItem.formOccurrenceId;
        const templateId =
          workItem.FormTemplateId || workItem.formTemplateId;

        if (!instanceId) {
          if (phase === "approve" || phase === "locked") {
            showAlert({
              title: "Form unavailable",
              message: "No submitted form instance is linked for review.",
              type: "info",
            });
            return;
          }
          if (!templateId) {
            showAlert({
              title: "Form unavailable",
              message: "No form template is mapped to this requirement.",
              type: "info",
            });
            return;
          }
          const created = await createInstance({
            FormTemplateId: templateId,
            StudyId: ctx.studyId,
            SiteId: ctx.siteId,
            Title:
              workItem.Title ||
              workItem.title ||
              workItem.TemplateName ||
              "Pharmacy form",
          });
          if (!created.success || !created.formInstanceId) {
            showAlert({
              title: "Form unavailable",
              message:
                created.message ||
                "Could not create form instance. Is e-Source licensed?",
              type: "info",
            });
            return;
          }
          instanceId = created.formInstanceId;
          const linked = await linkFormInstance({
            FormOccurrenceId: occurrenceId,
            FormInstanceId: instanceId,
          });
          if (!linked.success) {
            showAlert({
              title: "Form unavailable",
              message: linked.message || "Instance created but link failed.",
              type: "info",
            });
            return;
          }
        }

        const modalMode =
          phase === "approve"
            ? "review"
            : phase === "locked"
              ? "locked"
              : "fill";

        setFormFillModal({
          formInstanceId: instanceId,
          workItem: {
            ...workItem,
            FormInstanceId: instanceId,
            LinkedTxnId:
              workItem.LinkedTxnId ||
              workItem.linkedTxnId ||
              row?.ImpTxnId ||
              row?.impTxnId ||
              row?.LinkedTxnId ||
              null,
            StudyId: workItem.StudyId || ctx.studyId,
            SiteId: workItem.SiteId || ctx.siteId,
          },
          mode: modalMode,
        });
      } finally {
        setFormFillBusy(false);
      }
    },
    [ctx.siteId, ctx.studyId, showAlert],
  );

  const preferProductKey = String(preferImpProductId || "").toLowerCase();

  const user = useMemo(() => getUserData() || {}, []);
  const currentStaffId = String(user.StaffId || user.staffId || "");
  const currentStaffName = staffDisplayName(user);
  const currentStaffEmail = staffEmail(user);

  const [products, setProducts] = useState([]);
  const [storageUnits, setStorageUnits] = useState([]);
  const [storageRacks, setStorageRacks] = useState([]);
  const [receiveModes, setReceiveModes] = useState({
    hasBatchQuantity: true,
    hasIndividualKit: false,
    defaultMode: "BatchQuantity",
  });
  const [kitValidation, setKitValidation] = useState(null);
  const [kitValidateBusy, setKitValidateBusy] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [pasteBulk, setPasteBulk] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState(() =>
    emptyForm(currentStaffId, currentStaffName, currentStaffEmail),
  );
  const clientReceiptIdRef = useRef(newClientReceiptId());
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTab, setDetailTab] = useState("overview");
  const [detailTxnId, setDetailTxnId] = useState("");
  const [detailOverview, setDetailOverview] = useState(null);
  const [detailKits, setDetailKits] = useState([]);
  const [detailEvidence, setDetailEvidence] = useState([]);
  const [detailAudit, setDetailAudit] = useState([]);
  const [detailBusy, setDetailBusy] = useState(false);
  const [auditExportBusy, setAuditExportBusy] = useState(false);
  const [evidenceType, setEvidenceType] = useState("DeliveryNote");
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [releaseBusy, setReleaseBusy] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRow, setReviewRow] = useState(null);
  const [phoneScan, setPhoneScan] = useState(null); // { sessionId, token, expiresAt, url, originOverride }
  const [phoneScanBusy, setPhoneScanBusy] = useState(false);
  const [phoneScanHint, setPhoneScanHint] = useState("");
  const phonePollRef = useRef(null);
  /** Prepared batches not yet received — searchable in Add Receipt. */
  const [receivableBatches, setReceivableBatches] = useState([]);
  const [receivableBatchBusy, setReceivableBatchBusy] = useState(false);
  const [batchListQuery, setBatchListQuery] = useState("");

  const isIndividualKitReceipt =
    String(
      detailOverview?.InventoryTrackingMethod ||
        detailOverview?.inventoryTrackingMethod ||
        "",
    ) === "IndividualKit";

  const detailStatus = String(
    detailOverview?.ReceiptStatus || detailOverview?.receiptStatus || "",
  ).trim();

  const HELD_REVIEW_STATUSES = [
    "RequiresReview",
    "Hold",
    "Quarantined",
    "ReturnPending",
    "DestructionPending",
  ];

  const canReleaseReceipt =
    canReceive && HELD_REVIEW_STATUSES.includes(detailStatus);

  const isHeldReviewRow = (row) => {
    const status = String(
      row?.ReceiptStatus || row?.receiptStatus || "",
    ).trim();
    return HELD_REVIEW_STATUSES.includes(status);
  };

  const openHeldReview = (row) => {
    if (!row || !canReceive) return;
    setDetailOpen(false);
    setReviewRow(row);
    setReviewOpen(true);
  };

  const visibleDetailTabs = useMemo(
    () =>
      DETAIL_TABS.filter(
        (tab) => tab.id !== "kits" || isIndividualKitReceipt,
      ),
    [isIndividualKitReceipt],
  );

  /* Same lock pattern as Add receipt / CTMS workspace modals — page must not scroll. */
  useEffect(() => {
    if (!detailOpen && !showModal && !reviewOpen) return undefined;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbarGap =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.classList.add("modal-open");
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    if (scrollbarGap > 0) {
      document.body.style.paddingRight = `${scrollbarGap}px`;
    }
    return () => {
      document.body.classList.remove("modal-open");
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [detailOpen, showModal, reviewOpen]);

  const productOptions = useMemo(() => {
    const opts = (products || [])
      .filter((p) => {
        const tracking = String(
          p.InventoryTrackingMethod || p.inventoryTrackingMethod || "BatchQuantity",
        );
        if (tracking !== "BatchQuantity") return false;
        if (!preferProductKey) return true;
        return (
          String(p.ImpProductId || p.impProductId || "").toLowerCase() ===
          preferProductKey
        );
      })
      .map((p) => {
        const id = String(p.ImpProductId || p.impProductId || "");
        const code = p.ProductCode || p.productCode || "";
        const name = p.ProductName || p.productName || "";
        return {
          value: id,
          label: `${code} - ${name}`,
          row: p,
        };
      });
    return [{ value: "", label: "Select IMP" }, ...opts];
  }, [products, preferProductKey]);

  const selectedProduct = useMemo(() => {
    const opt = productOptions.find((o) => o.value === form.impProductId);
    return opt?.row || null;
  }, [productOptions, form.impProductId]);

  const kitParse = useMemo(
    () => parseKitNumbersDetailed(form.kitNumbersText),
    [form.kitNumbersText],
  );
  const parsedKitNumbers = kitParse.kits;
  const kitPasteDuplicates = kitParse.duplicates;

  /* Locked: AQ Receive is Individual Kit only (Batch/Quantity tabs removed). */
  const isIndividualKit = true;

  const kitLines = useMemo(() => {
    if (!isIndividualKit) return [];
    return parsedKitNumbers.map((kit) => {
      const key = kit.toUpperCase();
      const line = form.kitLineFields?.[key] || {};
      return {
        kitNumber: kit,
        batchNumber:
          line.batchNumber || form.defaultKitBatchNumber || "",
        expiryDate:
          line.expiryDate != null
            ? line.expiryDate
            : form.defaultKitExpiryDate || null,
        productLabel: line.productLabel || "",
        studyImpProductId: line.studyImpProductId || "",
        impProductId: line.impProductId || "",
      };
    });
  }, [
    isIndividualKit,
    parsedKitNumbers,
    form.kitLineFields,
    form.defaultKitBatchNumber,
    form.defaultKitExpiryDate,
  ]);

  const onKitStructuredFields = useCallback(({ kitNumber, batchNumber, expiryDate }) => {
    if (!kitNumber) return;
    const key = String(kitNumber).toUpperCase();
    setForm((prev) => {
      let nextExpiry = prev.defaultKitExpiryDate;
      const nextLine = { ...(prev.kitLineFields?.[key] || {}) };
      if (expiryDate) {
        const parts = String(expiryDate).slice(0, 10).split("-");
        if (parts.length === 3) {
          const d = new Date(
            Number(parts[0]),
            Number(parts[1]) - 1,
            Number(parts[2]),
          );
          nextLine.expiryDate = d;
          if (!nextExpiry) nextExpiry = d;
        }
      }
      return {
        ...prev,
        defaultKitBatchNumber:
          batchNumber && !prev.defaultKitBatchNumber
            ? batchNumber
            : prev.defaultKitBatchNumber,
        defaultKitExpiryDate: nextExpiry,
        kitLineFields: {
          ...prev.kitLineFields,
          [key]: nextLine,
        },
      };
    });
  }, []);

  const onKitExpiryChange = useCallback((kit, date) => {
    const key = String(kit || "").toUpperCase();
    if (!key) return;
    setForm((prev) => ({
      ...prev,
      kitLineFields: {
        ...prev.kitLineFields,
        [key]: {
          ...(prev.kitLineFields?.[key] || {}),
          expiryDate: date,
        },
      },
    }));
  }, []);

  const impTempMin =
    selectedProduct?.StorageTempMin ?? selectedProduct?.storageTempMin;
  const impTempMax =
    selectedProduct?.StorageTempMax ?? selectedProduct?.storageTempMax;
  const impTempUnit =
    selectedProduct?.StorageTempUnit ||
    selectedProduct?.storageTempUnit ||
    "C";

  const impRequiresRange = useMemo(() => {
    if (!selectedProduct) return "";
    return formatTempRange(
      impTempMin,
      impTempMax,
      impTempUnit,
      selectedProduct.StorageCondition ||
        selectedProduct.storageCondition ||
        "",
    );
  }, [selectedProduct, impTempMin, impTempMax, impTempUnit]);

  const storageOptions = useMemo(() => {
    const opts = (storageUnits || [])
      .map((u) => {
        const id = String(u.StorageUnitId || u.storageUnitId || "").trim();
        if (!id) return null;
        const code = u.UnitCode || u.unitCode || "";
        const name = u.UnitName || u.unitName || "";
        const loc = u.LocationLabel || u.locationLabel || "";
        const minC = u.MinTempC ?? u.minTempC;
        const maxC = u.MaxTempC ?? u.maxTempC;
        const storUnit = u.TempUnit || u.tempUnit || "C";
        const supports = formatTempRange(minC, maxC, storUnit);
        const compat = selectedProduct
          ? isStorageCompatibleWithImp({
              impMin: impTempMin,
              impMax: impTempMax,
              impUnit: impTempUnit,
              storageMin: minC,
              storageMax: maxC,
              storageUnit: storUnit,
            })
          : null;
        const hasImpRange =
          !isIndividualKit &&
          impTempMin != null &&
          impTempMin !== "" &&
          Number.isFinite(Number(impTempMin)) &&
          impTempMax != null &&
          impTempMax !== "" &&
          Number.isFinite(Number(impTempMax));
        const incompatible =
          Boolean(selectedProduct) && hasImpRange && compat !== true;
        const base = [code, name, loc].filter(Boolean).join(" | ") || id;
        return {
          value: id,
          label: supports ? `${base} [${supports}]` : base,
          disabled: false,
          row: u,
          supportsLabel: supports,
          compatible: compat,
          incompatible,
        };
      })
      .filter(Boolean);
    return [{ value: "", label: "Select storage location" }, ...opts];
  }, [
    storageUnits,
    selectedProduct,
    impTempMin,
    impTempMax,
    impTempUnit,
    isIndividualKit,
  ]);

  const selectedStorage = useMemo(() => {
    const opt = storageOptions.find((o) => o.value === form.storageUnitId);
    return opt || null;
  }, [storageOptions, form.storageUnitId]);

  const rackOptions = useMemo(() => {
    const unitId = String(form.storageUnitId || "").trim();
    const opts = (storageRacks || [])
      .filter(
        (r) =>
          String(r.StorageUnitId || r.storageUnitId || "").trim() === unitId,
      )
      .map((r) => {
        const id = String(r.StorageRackId || r.storageRackId || "").trim();
        if (!id) return null;
        const code = r.RackCode || r.rackCode || "";
        const name = r.RackName || r.rackName || "";
        return {
          value: id,
          label: [code, name].filter(Boolean).join(" — ") || id,
        };
      })
      .filter(Boolean);
    return [
      {
        value: "",
        label: opts.length ? "Select rack" : "No racks for this storage",
      },
      ...opts,
    ];
  }, [storageRacks, form.storageUnitId]);

  const unitHasRacks = rackOptions.length > 1;

  const storageCompatInfo = useMemo(() => {
    if (isIndividualKit) {
      if (!form.storageUnitId || !kitValidation?.summary) return null;
      if (kitValidation.summary.storageNeedsOverride) {
        return {
          compatible: false,
          message: "Selected storage is not compatible with one or more kits in this shipment. Allow storage override to continue.",
        };
      }
      if (kitValidation.summary.storageVerified) {
        return {
          compatible: true,
          message: "Storage requirements verified.",
        };
      }
      return null;
    }
    if (!selectedProduct || !form.storageUnitId) return null;
    const hasImpRange =
      impTempMin != null &&
      impTempMin !== "" &&
      Number.isFinite(Number(impTempMin)) &&
      impTempMax != null &&
      impTempMax !== "" &&
      Number.isFinite(Number(impTempMax));
    if (!hasImpRange) return null;
    const supports =
      selectedStorage?.supportsLabel ||
      formatTempRange(
        selectedStorage?.row?.MinTempC ?? selectedStorage?.row?.minTempC,
        selectedStorage?.row?.MaxTempC ?? selectedStorage?.row?.maxTempC,
        selectedStorage?.row?.TempUnit ||
          selectedStorage?.row?.tempUnit ||
          "C",
      );
    const compatible = selectedStorage?.compatible;
    return {
      supports: supports || "Not configured",
      requires: impRequiresRange || "Not configured",
      compatible,
    };
  }, [
    isIndividualKit,
    kitValidation,
    selectedProduct,
    form.storageUnitId,
    selectedStorage,
    impTempMin,
    impTempMax,
    impRequiresRange,
  ]);

  const storageIncompatible = isIndividualKit
    ? Boolean(kitValidation?.summary?.storageNeedsOverride)
    : Boolean(selectedStorage?.incompatible) ||
      selectedStorage?.compatible === false;

  const storageWarningText = useMemo(() => {
    if (!storageIncompatible) return "";
    if (isIndividualKit) {
      return (
        storageCompatInfo?.message ||
        "Selected storage is not compatible with one or more kits in this shipment."
      );
    }
    if (!storageCompatInfo) return "";
    return `Selected storage location supports ${storageCompatInfo.supports}, while this IMP requires ${storageCompatInfo.requires}.`;
  }, [storageIncompatible, storageCompatInfo, isIndividualKit]);

  const unitLabel = selectedProduct
    ? selectedProduct.UnitLabel || selectedProduct.unitLabel || ""
    : "";

  const tempUnitLabel = (() => {
    const u = String(impTempUnit || "C").toUpperCase();
    return u === "F" ? "\u00B0F" : "\u00B0C";
  })();

  const tempStatus = useMemo(() => {
    const tempText = String(form.temperatureWhenReceived || "").trim();
    if (!tempText) return null;
    return receiptTempStatus(tempText, impTempMin, impTempMax);
  }, [form.temperatureWhenReceived, impTempMin, impTempMax]);

  const loadProducts = useCallback(async () => {
    const result = await fetchImpProducts(null, null, true);
    if (result.success) setProducts(result.rows || []);
    else setProducts([]);
  }, []);

  const loadStorageUnits = useCallback(async () => {
    if (!ctx.studyId || !ctx.siteId) {
      setStorageUnits([]);
      setStorageRacks([]);
      return;
    }
    const result = await fetchStorageUnits(ctx.studyId, ctx.siteId);
    if (result.success) {
      setStorageUnits(result.rows || []);
      setStorageRacks(result.racks || []);
    } else {
      setStorageUnits([]);
      setStorageRacks([]);
    }
  }, [ctx.studyId, ctx.siteId]);

  useEffect(() => {
    loadProducts();
    loadStorageUnits();
  }, [loadProducts, loadStorageUnits]);

  const openReceiptDetail = async (txnId, { resetTab = true } = {}) => {
    if (!ctx.studyId || !ctx.siteId || !txnId) return;
    setDetailTxnId(String(txnId));
    setDetailOpen(true);
    if (resetTab) setDetailTab("overview");
    setDetailBusy(true);
    setFormError("");
    try {
      const result = await fetchReceiptDetail(ctx.studyId, ctx.siteId, txnId);
      if (!result.success) {
        setFormError(result.message || "Could not load receipt detail.");
        return;
      }
      const ds = result.ds || {};
      const tables = Object.keys(ds)
        .filter((k) => Array.isArray(ds[k]))
        .map((k) => ds[k]);
      const overview = tables[0]?.[0] || result.rows?.[0] || null;
      setDetailOverview(overview);
      setDetailKits(tables[1] || []);
      setDetailEvidence(tables[2] || []);
      setDetailAudit(tables[3] || []);

      const tracking =
        overview?.InventoryTrackingMethod ||
        overview?.inventoryTrackingMethod ||
        "";
      if (
        resetTab === false &&
        detailTab === "kits" &&
        String(tracking) !== "IndividualKit"
      ) {
        setDetailTab("overview");
      }
    } finally {
      setDetailBusy(false);
    }
  };

  useEffect(() => {
    if (!detailOpen) return;
    if (detailTab === "kits" && !isIndividualKitReceipt) {
      setDetailTab("overview");
    }
  }, [detailOpen, detailTab, isIndividualKitReceipt]);

  const onReleaseFromDetail = () => {
    if (!detailTxnId || releaseBusy || !canReleaseReceipt || !detailOverview)
      return;
    openHeldReview({ ...detailOverview, ImpTxnId: detailTxnId });
  };

  const onReviewConfirmedFromDetail = async ({ disposition, row } = {}) => {
    setReviewOpen(false);
    setReviewRow(null);
    setDetailOpen(false);
    setFormError("");
    await refresh();

    const batchId = String(
      row?.ImpBatchId ||
        row?.impBatchId ||
        detailOverview?.ImpBatchId ||
        detailOverview?.impBatchId ||
        "",
    );
    const sourceTxnId = String(
      row?.ImpTxnId || row?.impTxnId || detailTxnId || "",
    );

    if (disposition === "ReturnPending") {
      navigate("/epsf/imp/returns", {
        state: {
          openStockReturn: true,
          preferImpBatchId: batchId || null,
          preferSourceTxnId: sourceTxnId || null,
        },
      });
      return;
    }
    if (disposition === "DestructionPending") {
      navigate("/epsf/imp/destruction", {
        state: {
          openDestroy: true,
          preferImpBatchId: batchId || null,
          preferSourceTxnId: sourceTxnId || null,
        },
      });
    }
  };

  const onUploadEvidence = async () => {
    if (!evidenceFile || !detailTxnId || detailBusy) return;
    setDetailBusy(true);
    try {
      const result = await uploadReceiptEvidence({
        studyId: ctx.studyId,
        siteId: ctx.siteId,
        impTxnId: detailTxnId,
        evidenceType,
        file: evidenceFile,
      });
      if (!result.success) {
        setFormError(result.message || "Evidence upload failed.");
        return;
      }
      setEvidenceFile(null);
      await openReceiptDetail(detailTxnId, { resetTab: false });
      setDetailTab("documents");
    } finally {
      setDetailBusy(false);
    }
  };

  const renderCell = (row, field) => {
    if (field === "FormDue") {
      const phase = row.FormDuePhase || "none";
      if (phase === "none") return "—";
      const label =
        row.FormDueLabel ||
        (phase === "fill"
          ? "Fill form"
          : phase === "approve"
            ? "Approval pending"
            : "Approved");
      const toneClass =
        phase === "fill"
          ? "epsf-receipt-form-due--fill"
          : phase === "approve"
            ? "epsf-receipt-form-due--approve"
            : "epsf-receipt-form-due--locked";
      return (
        <button
          type="button"
          className={`epsf-receipt-form-due epsf-receipt-form-due--action ${toneClass}`}
          title={row.FormDue || label}
          disabled={formFillBusy}
          onClick={(e) => {
            e.stopPropagation();
            openReceiptFormFill(row);
          }}
        >
          {label}
        </button>
      );
    }
    if (field === "BatchNumber") {
      const txnId = row.ImpTxnId || row.impTxnId;
      const batch =
        String(row.BatchNumber || row.batchNumber || "").trim() || "—";
      if (!txnId) return batch;
      return (
        <button
          type="button"
          className="report-table__cell-link"
          title={isHeldReviewRow(row) ? "Review held stock" : "Open receipt"}
          onClick={() => {
            if (canReceive && isHeldReviewRow(row)) {
              openHeldReview(row);
              return;
            }
            openReceiptDetail(txnId);
          }}
        >
          {batch}
        </button>
      );
    }
    return renderImpTxnCell(row, field);
  };

  const openAdd = async () => {
    clientReceiptIdRef.current = newClientReceiptId();
    const next = emptyForm(currentStaffId, currentStaffName, currentStaffEmail);
    next.receiveMode = "IndividualKit";
    if (preferImpProductId) {
      next.impProductId = String(preferImpProductId);
    }
    setForm(next);
    setFormError("");
    setFieldErrors({});
    setError("");
    setKitValidation(null);
    setReceivableBatches([]);
    setBatchListQuery("");
    await Promise.all([loadProducts(), loadStorageUnits()]);
    setShowModal(true);
    if (ctx.studyId) {
      setReceivableBatchBusy(true);
      try {
        const [mapRes, recvRes] = await Promise.all([
          fetchStudyImpKitMaps(ctx.studyId, ctx.siteId || null).catch(
            () => null,
          ),
          fetchReceivableBatches(ctx.studyId, ctx.siteId || null).catch(
            () => null,
          ),
        ]);
        const mapRows = mapRes?.success ? mapRes.rows || [] : [];
        const recvRows = recvRes?.success ? recvRes.rows || [] : [];
        if (mapRows.length) {
          setReceivableBatches(
            buildPreparedBatchOptions(
              mapRows,
              indexReceivableBatches(recvRows),
            ),
          );
        } else if (recvRows.length) {
          setReceivableBatches(buildReceivableOnlyBatchOptions(recvRows));
        } else {
          setReceivableBatches([]);
        }
      } catch {
        setReceivableBatches([]);
      } finally {
        setReceivableBatchBusy(false);
      }
    }
  };

  const filteredReceivableBatches = useMemo(() => {
    const q = batchListQuery.trim().toLowerCase();
    if (!q) return receivableBatches;
    return receivableBatches.filter((opt) => {
      const hay = `${opt.batchNumber || ""} ${opt.expiryIso || ""} ${
        opt.label || ""
      } ${expiryChipLabel(opt.expiryIso)} ${opt.mappingTone || ""} mapped partial`.toLowerCase();
      return hay.includes(q);
    });
  }, [receivableBatches, batchListQuery]);

  const selectedReceivableBatch = useMemo(() => {
    const typed = String(form.defaultKitBatchNumber || "").trim();
    if (!typed) return null;
    const typedExp = toApiDateString(form.defaultKitExpiryDate) || "";
    return (
      receivableBatches.find(
        (o) => o.value === receivableBatchKey(typed, typedExp),
      ) ||
      receivableBatches.find((o) => o.batchNumber === typed) ||
      null
    );
  }, [
    receivableBatches,
    form.defaultKitBatchNumber,
    form.defaultKitExpiryDate,
  ]);

  const onSelectReceivableBatch = useCallback((opt) => {
    if (!opt) {
      setForm((prev) => ({
        ...prev,
        defaultKitBatchNumber: "",
        defaultKitExpiryDate: null,
        kitNumbersText: "",
        kitLineFields: {},
      }));
      setFieldErrors((prev) => ({
        ...prev,
        defaultKitBatchNumber: "",
        defaultKitExpiryDate: "",
        kitNumbersText: "",
      }));
      return;
    }

    const batchNumber = String(opt.batchNumber || opt.value || "")
      .trim()
      .slice(0, 100);
    const expiryIso = String(opt.expiryIso || "").trim().slice(0, 10);
    const expiryDate = expiryIso ? parseIsoDateLocal(expiryIso) : null;
    const details = Array.isArray(opt.kitDetails) ? opt.kitDetails : [];
    const kits = details.length
      ? details.map((d) => d.kitNumber)
      : Array.isArray(opt.kits)
        ? opt.kits
        : [];
    const nextFields = {};
    if (details.length) {
      details.forEach((d) => {
        const text = String(d?.kitNumber || "").trim();
        if (!text) return;
        const kitExpIso = String(d.expiryIso || expiryIso || "").trim().slice(0, 10);
        nextFields[text.toUpperCase()] = {
          batchNumber,
          expiryDate: kitExpIso ? parseIsoDateLocal(kitExpIso) : expiryDate,
          productLabel: d.productLabel || "Mapped IMP",
          studyImpProductId: d.studyImpProductId || "",
          impProductId: d.impProductId || "",
        };
      });
    } else {
      kits.forEach((kit) => {
        const text = String(kit || "").trim();
        if (!text) return;
        nextFields[text.toUpperCase()] = {
          batchNumber,
          expiryDate,
          productLabel: "Mapped IMP",
        };
      });
    }

    setForm((prev) => ({
      ...prev,
      defaultKitBatchNumber: batchNumber,
      defaultKitExpiryDate: expiryDate,
      kitNumbersText: joinKitNumbers(kits),
      kitLineFields: nextFields,
    }));
    setFieldErrors((prev) => ({
      ...prev,
      defaultKitBatchNumber: "",
      defaultKitExpiryDate: "",
      kitNumbersText: "",
    }));
  }, []);

  const closeModal = () => {
    if (busy) return;
    const hasTypedKits = Boolean(String(form.kitNumbersText || "").trim());
    const finishClose = () => {
      if (phoneScan?.sessionId) {
        closeReceiveScanSession(phoneScan.sessionId).catch(() => {});
      }
      if (phonePollRef.current) {
        window.clearInterval(phonePollRef.current);
        phonePollRef.current = null;
      }
      setPhoneScan(null);
      setPhoneScanHint("");
      setPasteBulk("");
      setShowModal(false);
      setFormError("");
      setFieldErrors({});
      setKitValidation(null);
      setReceivableBatches([]);
      setBatchListQuery("");
      if (overlay) onOverlayClose?.();
    };
    if (hasTypedKits) {
      showAlert({
        title: "Discard selected kits?",
        message:
          "Closing will clear the selected prepared batch and its kits from this receipt.",
        type: "warning",
        buttonText: "Discard",
        cancelText: "Keep editing",
        onConfirm: () => {
          hideAlert();
          finishClose();
        },
      });
      return;
    }
    finishClose();
  };

  const buildPhoneScanUrl = (shortCode, originOverride, apiOverride) => {
    const code = String(shortCode || "").trim().toUpperCase();
    const origin = String(originOverride || "").replace(/\/$/, "");
    if (!origin || !code) return "";
    let url = `${origin}/s/${encodeURIComponent(code)}`;
    const apiBase = String(apiOverride || "").trim();
    if (apiBase) {
      url += `?api=${encodeURIComponent(apiBase.replace(/\/?$/, "/"))}`;
    }
    return url;
  };

  const applyPhoneScanEvents = useCallback((events) => {
    if (!Array.isArray(events) || !events.length) return;
    setForm((prev) => {
      const existing = parseKitNumbers(prev.kitNumbersText);
      const existingSet = new Set(existing.map((k) => k.toUpperCase()));
      const nextKits = [...existing];
      let added = 0;
      let dupes = 0;
      let nextBatch = prev.defaultKitBatchNumber;
      let nextExpiry = prev.defaultKitExpiryDate;
      events.forEach((ev) => {
        const kit = String(ev.kitNumber || "").trim();
        if (!kit) return;
        const key = kit.toUpperCase();
        if (existingSet.has(key)) {
          dupes += 1;
          return;
        }
        existingSet.add(key);
        nextKits.push(kit);
        added += 1;
        if (ev.batchNumber && !nextBatch) nextBatch = ev.batchNumber;
        if (ev.expiryDate && !nextExpiry) {
          const text = String(ev.expiryDate).slice(0, 10);
          const parts = text.split("-");
          if (parts.length === 3) {
            nextExpiry = new Date(
              Number(parts[0]),
              Number(parts[1]) - 1,
              Number(parts[2]),
            );
          }
        }
      });
      if (added) {
        setPhoneScanHint(
          dupes
            ? `Added ${added} kit(s) from phone · ${dupes} already staged`
            : `Added ${added} kit(s) from phone`,
        );
      } else if (dupes) {
        setPhoneScanHint(`Phone sent ${dupes} kit(s) already staged`);
      }
      return {
        ...prev,
        kitNumbersText: joinKitNumbers(nextKits),
        defaultKitBatchNumber: nextBatch || "",
        defaultKitExpiryDate: nextExpiry || null,
      };
    });
  }, []);

  const stopPhoneScanSession = useCallback(async () => {
    if (phonePollRef.current) {
      window.clearInterval(phonePollRef.current);
      phonePollRef.current = null;
    }
    const id = phoneScan?.sessionId;
    setPhoneScan(null);
    setPhoneScanHint("");
    if (id) {
      try {
        await closeReceiveScanSession(id);
      } catch {
        /* ignore */
      }
    }
  }, [phoneScan?.sessionId]);

  const startPhoneScanSession = useCallback(async () => {
    if (!ENABLE_PHONE_REMOTE_SCAN || !ctx.studyId || !ctx.siteId || phoneScanBusy) {
      return;
    }
    setPhoneScanBusy(true);
    setPhoneScanHint("");
    try {
      if (phoneScan?.sessionId) {
        await closeReceiveScanSession(phoneScan.sessionId).catch(() => {});
      }
      const res = await createReceiveScanSession(ctx.studyId, ctx.siteId);
      if (!res.success || !res.session?.accessToken) {
        setPhoneScanHint(res.message || "Could not start phone scan session.");
        return;
      }
      const code = res.session.shortCode || res.session.accessToken;
      // Always use the current site origin (train/LIVE/public URL) — never LAN IP.
      const siteOrigin = String(window.location.origin || "").replace(/\/$/, "");
      const phoneUrl = buildPhoneScanUrl(code, siteOrigin, "");
      setPhoneScan({
        sessionId: res.session.scanSessionId,
        token: code,
        shortCode: code,
        expiresAt: res.session.expiresAt,
        localUrl: phoneUrl,
        url: phoneUrl,
        originOverride: siteOrigin,
        apiOverride: "",
        lanIp: "",
        lanCandidates: [],
        isLocalDev: false,
      });
      setPhoneScanHint("Scan this QR with your phone to add kits.");
      if (phonePollRef.current) window.clearInterval(phonePollRef.current);
      phonePollRef.current = window.setInterval(async () => {
        try {
          const poll = await pollReceiveScanEvents(res.session.scanSessionId);
          if (!poll.success) return;
          if (poll.events?.length) applyPhoneScanEvents(poll.events);
          if (poll.session && !poll.session.isActive) {
            window.clearInterval(phonePollRef.current);
            phonePollRef.current = null;
            setPhoneScanHint("Phone scan session ended.");
            setPhoneScan(null);
          }
        } catch {
          /* keep polling */
        }
      }, 1500);
    } catch (e) {
      setPhoneScanHint(e?.message || "Could not start phone scan session.");
    } finally {
      setPhoneScanBusy(false);
    }
  }, [
    ctx.studyId,
    ctx.siteId,
    phoneScanBusy,
    phoneScan?.sessionId,
    applyPhoneScanEvents,
  ]);

  useEffect(() => {
    return () => {
      if (phonePollRef.current) {
        window.clearInterval(phonePollRef.current);
        phonePollRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!overlay) return undefined;
    openAdd();
    return undefined;
    // Mount-once for overlay launch from Inventory.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay]);

  // Prefer product context from inventory overlay (kit-only receive).
  useEffect(() => {
    if (!overlay || !showModal) return;
    const preferred = String(preferImpProductId || "").trim();
    if (!preferred) return;
    setForm((prev) =>
      prev.impProductId === preferred
        ? prev
        : { ...prev, receiveMode: "IndividualKit", impProductId: preferred },
    );
  }, [overlay, showModal, preferImpProductId]);

  useEffect(() => {
    if (!showModal || !isIndividualKit || !ctx.studyId || !ctx.siteId) {
      setKitValidation(null);
      return undefined;
    }
    if (!parsedKitNumbers.length) {
      setKitValidation(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setKitValidateBusy(true);
      try {
        const kitLinesPayload = kitLines.map((l) => ({
          KitNumber: l.kitNumber,
          BatchNumber: String(l.batchNumber || "").trim() || null,
          ExpiryDate: toApiDateString(l.expiryDate) || null,
        }));
        const result = await validateKitShipment(
          ctx.studyId,
          ctx.siteId,
          parsedKitNumbers,
          form.storageUnitId || null,
          kitLinesPayload,
        );
        if (!cancelled) {
          setKitValidation(
            result.success
              ? result
              : {
                  success: false,
                  message: result.message,
                  summary: null,
                  kitRows: [],
                },
          );
        }
      } finally {
        if (!cancelled) setKitValidateBusy(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    showModal,
    isIndividualKit,
    ctx.studyId,
    ctx.siteId,
    form.kitNumbersText,
    form.storageUnitId,
    kitLines,
    parsedKitNumbers,
  ]);

  const onSelectProduct = (opt) => {
    setForm((prev) => ({
      ...prev,
      impProductId: opt?.value || "",
      kitNumbersText: "",
      kitLineFields: {},
      quantity: "",
      storageUnitId: "",
      storageRackId: "",
      allowStorageOverride: false,
      storageOverrideReason: "",
      storageOverrideJustification: "",
    }));
    setFieldErrors((prev) => ({
      ...prev,
      impProductId: "",
      kitNumbersText: "",
      quantity: "",
      storageUnitId: "",
      storageRackId: "",
      storageOverrideReason: "",
      storageOverrideJustification: "",
    }));
  };

  const setReceiveMode = (mode) => {
    setForm((prev) => ({
      ...prev,
      receiveMode: mode,
      impProductId: mode === "IndividualKit" ? "" : prev.impProductId,
      quantity: mode === "IndividualKit" ? "" : prev.quantity,
      kitNumbersText: mode === "BatchQuantity" ? "" : prev.kitNumbersText,
      kitLineFields: mode === "BatchQuantity" ? {} : prev.kitLineFields,
      allowStorageOverride: false,
      storageOverrideReason: "",
      storageOverrideJustification: "",
    }));
    setKitValidation(null);
    setFieldErrors({});
    setFormError("");
  };

  const combineLocal = (date, timeHHmm) => {
    if (!date) return null;
    const d = new Date(date);
    const [h, m] = String(timeHHmm || "00:00")
      .split(":")
      .map((x) => Number(x));
    d.setHours(Number.isFinite(h) ? h : 0, Number.isFinite(m) ? m : 0, 0, 0);
    return d;
  };

  const validateClient = () => {
    const errors = {};

    if (isIndividualKit) {
      if (!parsedKitNumbers.length) {
        errors.kitNumbersText =
          "Select a prepared batch to load its mapped kits.";
      } else {
        const tooLong = parsedKitNumbers.find((k) => k.length > 100);
        if (tooLong) {
          errors.kitNumbersText = `Kit number exceeds 100 characters: ${tooLong}`;
        }
        if (kitPasteDuplicates.length) {
          errors.kitNumbersText = `Duplicate kit numbers in this receipt: ${kitPasteDuplicates.join(", ")}`;
        }
        if (kitValidation?.summary?.alreadyReceivedCount > 0) {
          const alreadyKits = (kitValidation.kitRows || [])
            .filter((r) => {
              const status = String(
                r.ValidationStatus || r.validationStatus || "",
              );
              return status === "AlreadyReceived";
            })
            .map((r) => String(r.KitNumber || r.kitNumber || "").trim())
            .filter(Boolean);
          errors.kitNumbersText = alreadyKits.length
            ? `Already received for this study: ${alreadyKits.join(", ")}`
            : "One or more kits were already received for this study.";
        }
        const missingExpiry = kitLines.find((l) => !l.expiryDate);
        if (missingExpiry) {
          errors.kitNumbersText =
            "Each kit needs an expiry date. Select a prepared batch with expiry set.";
        }
      }

      if (!String(form.defaultKitBatchNumber || "").trim()) {
        errors.defaultKitBatchNumber =
          "Select an IMP-mapped prepared batch from the list.";
      }
      if (!form.defaultKitExpiryDate) {
        errors.defaultKitExpiryDate = "Batch expiry is required.";
      } else {
        const exp = new Date(form.defaultKitExpiryDate);
        exp.setHours(0, 0, 0, 0);
        if (exp < startOfToday()) {
          errors.defaultKitExpiryDate = "Batch expiry cannot be in the past.";
        }
      }
    } else {
      if (!form.impProductId) {
        errors.impProductId = "Please select an IMP.";
      }
      const batch = String(form.batchNumber || "").trim();
      if (!batch) errors.batchNumber = "Batch / Lot number is required.";
      else if (batch.length > 100)
        errors.batchNumber = "Batch / Lot number cannot exceed 100 characters.";

      if (!form.expiryDate) errors.expiryDate = "Expiry date is required.";
      else {
        const exp = new Date(form.expiryDate);
        exp.setHours(0, 0, 0, 0);
        if (exp < startOfToday())
          errors.expiryDate = "This batch has already expired.";
      }

      const qty = Number(form.quantity);
      if (form.quantity === "" || form.quantity == null)
        errors.quantity = "Received quantity is required.";
      else if (!Number.isFinite(qty) || qty <= 0)
        errors.quantity = "Received quantity must be greater than zero.";
    }

    const ref = String(form.referenceNo || "").trim();
    if (ref.length > 100)
      errors.referenceNo = "Shipment / reference cannot exceed 100 characters.";

    if (!form.receivedDate)
      errors.receivedDate = "Received date is required.";
    else {
      const recv = combineLocal(form.receivedDate, form.receivedTime);
      if (recv && recv.getTime() > Date.now() + 5 * 60 * 1000)
        errors.receivedDate = "Received date cannot be in the future.";
    }

    if (!form.receivedTime)
      errors.receivedTime = "Received time is required.";

    if (!form.receivedByStaffId)
      errors.receivedByStaffId = "Please select who received the IMP.";

    if (!form.storageUnitId) {
      errors.storageUnitId = "Please select a storage location.";
    } else if (storageIncompatible) {
      if (!form.allowStorageOverride) {
        errors.storageUnitId = isIndividualKit
          ? "Storage location is not compatible with this kit shipment. Allow storage override to continue."
          : "Storage location is not compatible with this IMP. Allow storage override to continue.";
      } else {
        if (!form.storageOverrideReason) {
          errors.storageOverrideReason = "Please select an override reason.";
        }
        const just = String(form.storageOverrideJustification || "").trim();
        if (!just) {
          errors.storageOverrideJustification =
            "Please provide an override justification.";
        } else if (just.length > 1000) {
          errors.storageOverrideJustification =
            "Override justification cannot exceed 1000 characters.";
        }
      }
    }

    if (unitHasRacks && !form.storageRackId) {
      errors.storageRackId = "Please select a rack for this storage location.";
    }

    if (!form.conditionCode)
      errors.conditionCode = "Please select a condition on receipt.";
    else if (form.conditionCode !== "Acceptable") {
      const details = String(form.conditionDetails || "").trim();
      if (!details)
        errors.conditionDetails = "Please provide issue details.";
      else if (details.length > 500)
        errors.conditionDetails =
          "Issue details cannot exceed 500 characters.";
    }

    const tempText = String(form.temperatureWhenReceived || "").trim();
    if (tempText) {
      const t = Number(tempText);
      if (!Number.isFinite(t))
        errors.temperatureWhenReceived =
          "Temperature when received must be a valid number.";
    }

    return errors;
  };

  const postReceiptAfterAuth = async (receiptNotes) => {
    const qty = isIndividualKit
      ? parsedKitNumbers.length
      : Number(form.quantity);
    const tempText = String(form.temperatureOnReceipt || "").trim();
    const whenReceivedText = String(form.temperatureWhenReceived || "").trim();
    const txnAt = toApiDateTimeString(form.receivedDate, form.receivedTime);
    const kitLinesPayload = isIndividualKit
      ? kitLines.map((l) => ({
          KitNumber: l.kitNumber,
          BatchNumber: String(l.batchNumber || "").trim(),
          ExpiryDate: toApiDateString(l.expiryDate),
        }))
      : null;

    setBusy(true);
    try {
      const result = await postImpReceipt({
        StudyId: ctx.studyId,
        SiteId: ctx.siteId,
        ImpTxnId: clientReceiptIdRef.current,
        ClientRequestId: clientReceiptIdRef.current,
        ImpProductId: isIndividualKit ? null : form.impProductId || null,
        BatchNumber: isIndividualKit
          ? String(form.defaultKitBatchNumber || "").trim() || null
          : String(form.batchNumber || "").trim(),
        ExpiryDate: isIndividualKit
          ? toApiDateString(form.defaultKitExpiryDate) || null
          : toApiDateString(form.expiryDate),
        Quantity: qty,
        KitNumbers: isIndividualKit
          ? JSON.stringify(parsedKitNumbers)
          : null,
        KitLinesJson: kitLinesPayload
          ? JSON.stringify(kitLinesPayload)
          : null,
        InventoryTrackingMethod: isIndividualKit
          ? "IndividualKit"
          : "BatchQuantity",
        ReferenceNo: String(form.referenceNo || "").trim(),
        Notes: String(receiptNotes || "").trim(),
        TxnAt: txnAt,
        ConditionCode: form.conditionCode,
        ConditionDetails:
          form.conditionCode === "Acceptable"
            ? ""
            : String(form.conditionDetails || "").trim(),
        StorageUnitId: form.storageUnitId || null,
        StorageRackId: form.storageRackId || null,
        TemperatureOnReceipt: tempText ? Number(tempText) : null,
        TemperatureWhenReceived: whenReceivedText
          ? Number(whenReceivedText)
          : null,
        ReceivedByStaffId: form.receivedByStaffId,
        StorageCompatibilityOverride: Boolean(
          storageIncompatible && form.allowStorageOverride,
        ),
        StorageOverrideReason:
          storageIncompatible && form.allowStorageOverride
            ? form.storageOverrideReason
            : "",
        StorageOverrideJustification:
          storageIncompatible && form.allowStorageOverride
            ? String(form.storageOverrideJustification || "").trim()
            : "",
      });
      if (!result.success) {
        showAlert({
          title: "Receipt failed",
          message: result.message || "Receipt failed.",
          type: "danger",
        });
        return;
      }
      const txnId =
        result.rows?.[0]?.ImpTxnId || result.rows?.[0]?.impTxnId || null;
      try {
        await ensureFormOccurrence({
          StudyId: ctx.studyId,
          SiteId: ctx.siteId,
          TriggerCode: "OnReceipt",
          LinkedTxnId: txnId,
        });
      } catch {
        /* enrichment only */
      }
      clientReceiptIdRef.current = newClientReceiptId();
      setShowModal(false);
      setPasteBulk("");
      setForm(emptyForm(currentStaffId, currentStaffName, currentStaffEmail));
      setFieldErrors({});
      await refresh();
      if (overlay) onOverlaySuccess?.();
      if (result.message && /review required/i.test(result.message)) {
        setError(result.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setFormError("");
    setError("");
    if (!canReceive) {
      showAlert({
        title: "Not allowed",
        message: "You do not have permission to receive IMP.",
        type: "warning",
      });
      return;
    }
    if (!ctx.studyId || !ctx.siteId) {
      showAlert({
        title: "Study and site required",
        message: "Select study and site in the breadcrumb bar.",
        type: "warning",
      });
      return;
    }
    const errors = validateClient();
    setFieldErrors(errors);
    if (Object.keys(errors).length) {
      window.requestAnimationFrame(() => {
        const el = document.querySelector(
          ".epsf-receipt-modal .epsf-form-field-error, .epsf-receipt-modal .epsf-kit-table-input__error",
        );
        el?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
      });
      return;
    }

    const kitCount = isIndividualKit
      ? parsedKitNumbers.length
      : Number(form.quantity) || 0;

    showAlert({
      title: "Confirm receipt",
      message: isIndividualKit
        ? `Are you sure you want to post these ${kitCount} kit${kitCount === 1 ? "" : "s"}? You must authenticate to complete the final submission.`
        : `Are you sure you want to post quantity ${kitCount}? You must authenticate to complete the final submission.`,
      type: "warning",
      buttonText: "Continue",
      cancelText: "Cancel",
      onConfirm: async () => {
        hideAlert();
        const sign = await openESignatureConfirmModal({
          title: "Sign receipt",
        });
        if (
          !sign ||
          sign.cancelled ||
          Number(sign.Success ?? sign.success ?? 0) !== 1 ||
          !sign.SignatureBase64
        ) {
          return;
        }
        const auth = await openAuthModal({
          introMessage:
            "Re-authenticate to post this IMP receipt. Add receipt notes if needed.",
          showDescription: true,
          requireDescription: false,
          descriptionLabel: "Receipt notes",
          descriptionPlaceholder: "Optional notes for this receipt…",
          descriptionMaxLength: 1000,
        });
        if (
          !auth ||
          auth.cancelled ||
          Number(auth.Success ?? auth.success ?? 0) !== 1
        ) {
          return;
        }
        const notes = String(auth.Description || auth.description || "").trim();
        await postReceiptAfterAuth(notes);
      },
    });
  };

  const applyPasteBulk = () => {
    if (busy) return;
    const parsed = parseKitNumbers(pasteBulk);
    if (!parsed.length) return;
    const existing = new Set(parsedKitNumbers.map((k) => k.toUpperCase()));
    const fresh = parsed.filter((k) => !existing.has(k.toUpperCase()));
    if (!fresh.length) {
      setFieldErrors((prev) => ({
        ...prev,
        kitNumbersText: "Those kit numbers are already in the list.",
      }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      kitNumbersText: joinKitNumbers([
        ...parseKitNumbers(prev.kitNumbersText),
        ...fresh,
      ]),
    }));
    setPasteBulk("");
    setFormError("");
    setFieldErrors((prev) => ({ ...prev, kitNumbersText: "" }));
  };

  const setField = (key) => (ev) => {
    const value = ev.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const strengthLabel = selectedProduct
    ? formatStrengthDisplay(
        selectedProduct.StrengthValue ?? selectedProduct.strengthValue,
        selectedProduct.StrengthUnit || selectedProduct.strengthUnit,
        selectedProduct.Strength || selectedProduct.strength || "",
      )
    : "";

  const storageLabel = selectedProduct
    ? formatStorageTempRange(
        selectedProduct.StorageTempMin ?? selectedProduct.storageTempMin,
        selectedProduct.StorageTempMax ?? selectedProduct.storageTempMax,
        selectedProduct.StorageTempUnit || selectedProduct.storageTempUnit,
        selectedProduct.StorageCondition || selectedProduct.storageCondition || "",
      )
    : "";

  return (
    <EpsfAccessGate allow={allow} loading={loading}>
      {!overlay ? (
      <div className="epsf-list-page">
        <EpsfPageBreadcrumb
          items={[
            { label: "ePSF", path: "/epsf/imp/inventory" },
            { label: "IMP" },
            { label: "Receive" },
          ]}
          actions={[
            { icon: "fas fa-refresh", label: "Refresh", onClick: refresh },
            ...(canReceive
              ? [{ icon: "fas fa-plus", label: "Add receipt", onClick: openAdd }]
              : []),
          ]}
        />

        {error ? <p className="epsf-list-page__error">{error}</p> : null}

        <div className="epsf-report-wrap">
          <ReportTable
            rows={receiptRows}
            loading={listLoading}
            fieldOptions={IMP_RECEIPT_FIELD_OPTIONS}
            defaultColumns={IMP_RECEIPT_DEFAULT_COLUMNS}
            getFieldLabel={(field) =>
              IMP_RECEIPT_FIELD_OPTIONS.find((x) => x.key === field)?.label ||
              field
            }
            renderCell={renderCell}
            getRowKey={getImpTxnRowKey}
            persistLayoutKey="epsf-imp-receipt-txns-v10"
            emptyMessage={
              !ctx.studyId || !ctx.siteId
                ? "Select study and site in the breadcrumb bar."
                : "No receipts for this study and site yet."
            }
            pageSizeOptions={[10, 25, 50, 100]}
            columnSearchMode="client"
          />
        </div>

        {detailOpen
          ? createPortal(
              <div
                className="modal fade show d-block epsf-modal epsf-receipt-detail-modal"
                tabIndex="-1"
                role="dialog"
                aria-modal="true"
              >
            <div className="modal-dialog modal-xl epsf-receipt-detail-modal__dialog">
              <div className="modal-content epsf-receipt-detail-modal__content">
                <div className="modal-header epsf-receipt-detail-modal__header">
                  <div className="epsf-receipt-detail-modal__title-block">
                    <p className="epsf-receipt-detail-modal__eyebrow">
                      Receipt details
                    </p>
                    {(() => {
                      const code = String(
                        detailOverview?.ProductCode ||
                          detailOverview?.productCode ||
                          "",
                      ).trim();
                      const name = String(
                        detailOverview?.ProductName ||
                          detailOverview?.productName ||
                          "",
                      ).trim();
                      if (!code && !name) {
                        return (
                          <h5 className="modal-title mb-0">Loading receipt…</h5>
                        );
                      }
                      return renderImpProductCell({
                        ProductCode: code,
                        ProductName: name,
                      });
                    })()}
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setDetailOpen(false)}
                  />
                </div>

                <div className="epsf-receipt-detail-modal__tabs" role="tablist">
                  {visibleDetailTabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={detailTab === tab.id}
                      className={`btn btn-sm epsf-receipt-detail-modal__tab ${
                        detailTab === tab.id
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`}
                      onClick={() => setDetailTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="modal-body epsf-receipt-detail-modal__body">
                  {detailBusy && !detailOverview ? (
                    <p className="epsf-receipt-detail-modal__empty">
                      Loading receipt…
                    </p>
                  ) : detailTab === "overview" ? (
                    (() => {
                      const ov = detailOverview || {};
                      const reviewReason = String(
                        ov.ReviewReason || ov.reviewReason || "",
                      ).trim();
                      const overrideReason = String(
                        ov.StorageOverrideReason ||
                          ov.storageOverrideReason ||
                          "",
                      ).trim();
                      const conditionDetails = String(
                        ov.ConditionDetails || ov.conditionDetails || "",
                      ).trim();
                      const notes = String(ov.Notes || ov.notes || "").trim();
                      const qty = ov.Quantity ?? ov.quantity;
                      const unit = ov.UnitLabel || ov.unitLabel || "";
                      const temp =
                        ov.TemperatureOnReceipt ?? ov.temperatureOnReceipt;
                      const tempWhenReceived =
                        ov.TemperatureWhenReceived ??
                        ov.temperatureWhenReceived;
                      const shipment = ov.ReferenceNo || ov.referenceNo || "";
                      const kitCount =
                        detailKits.length ||
                        ov.KitCount ||
                        ov.kitCount ||
                        0;

                      return (
                        <div className="epsf-receipt-detail">
                          <div className="epsf-receipt-detail__badges">
                            {renderReceiptStatusBadge(
                              ov.ReceiptStatus || ov.receiptStatus,
                            )}
                            {renderConditionBadge(
                              ov.ConditionCode || ov.conditionCode,
                            )}
                          </div>

                          <dl className="epsf-receipt-detail__grid">
                            <div className="epsf-receipt-detail__item">
                              <dt>Received</dt>
                              <dd>
                                {ov.TxnAt ? formatTxnWhen(ov) : "—"}
                              </dd>
                            </div>
                            <div className="epsf-receipt-detail__item">
                              <dt>Batch / Lot</dt>
                              <dd>{ov.BatchNumber || ov.batchNumber || "—"}</dd>
                            </div>
                            <div className="epsf-receipt-detail__item">
                              <dt>Expiry</dt>
                              <dd>
                                {formatExpiryDisplay(
                                  ov.ExpiryDate || ov.expiryDate,
                                ) || "—"}
                              </dd>
                            </div>
                            <div className="epsf-receipt-detail__item">
                              <dt>Quantity</dt>
                              <dd>
                                {qty == null || qty === ""
                                  ? "—"
                                  : unit
                                    ? `${qty} ${unit}`
                                    : String(qty)}
                              </dd>
                            </div>
                            <div className="epsf-receipt-detail__item">
                              <dt>Kits</dt>
                              <dd>
                                {Number(kitCount) > 0
                                  ? String(kitCount)
                                  : "—"}
                              </dd>
                            </div>
                            <div className="epsf-receipt-detail__item">
                              <dt>Shipment / Ref</dt>
                              <dd>{shipment || "—"}</dd>
                            </div>
                            {temp != null && temp !== "" ? (
                              <div className="epsf-receipt-detail__item">
                                <dt>Temperature</dt>
                                <dd>{`${temp} °C`}</dd>
                              </div>
                            ) : null}
                            {tempWhenReceived != null &&
                            tempWhenReceived !== "" ? (
                              <div className="epsf-receipt-detail__item">
                                <dt>Temperature when received</dt>
                                <dd>{`${tempWhenReceived} °C`}</dd>
                              </div>
                            ) : null}
                            {HELD_REVIEW_STATUSES.includes(detailStatus) ||
                            reviewReason ? (
                              <div className="epsf-receipt-detail__item epsf-receipt-detail__item--wide">
                                <dt>Hold reason</dt>
                                <dd>
                                  {reviewReason
                                    ? formatReviewReasonLabel(reviewReason)
                                    : "Not specified"}
                                </dd>
                              </div>
                            ) : null}
                            {overrideReason ? (
                              <div className="epsf-receipt-detail__item epsf-receipt-detail__item--wide">
                                <dt>Storage override</dt>
                                <dd>{overrideReasonLabel(overrideReason)}</dd>
                              </div>
                            ) : null}
                            {conditionDetails ? (
                              <div className="epsf-receipt-detail__item epsf-receipt-detail__item--wide">
                                <dt>Condition notes</dt>
                                <dd>{conditionDetails}</dd>
                              </div>
                            ) : null}
                            {notes ? (
                              <div className="epsf-receipt-detail__item epsf-receipt-detail__item--wide">
                                <dt>Receipt notes</dt>
                                <dd>{notes}</dd>
                              </div>
                            ) : null}
                          </dl>
                        </div>
                      );
                    })()
                  ) : detailTab === "kits" ? (
                    detailKits.length ? (
                      <ul className="epsf-receipt-detail__kit-list">
                        {detailKits.map((k) => {
                          const kitNo = k.KitNumber || k.kitNumber || "—";
                          const status = k.Status || k.status || "";
                          return (
                            <li
                              key={k.ImpKitId || k.impKitId || kitNo}
                              className="epsf-receipt-detail__kit-row"
                            >
                              <span className="epsf-receipt-detail__kit-no">
                                {kitNo}
                              </span>
                              {status
                                ? renderReceiptStatusBadge(status)
                                : null}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="epsf-receipt-detail-modal__empty">
                        No kits on this receipt.
                      </p>
                    )
                  ) : detailTab === "audit" ? (
                    detailAudit.length ? (
                      <div className="epsf-receipt-detail__audit">
                        <div className="d-flex justify-content-end mb-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={auditExportBusy || !detailAudit.length}
                            title="Export audit log to Excel"
                            onClick={async () => {
                              if (auditExportBusy || !detailAudit.length) return;
                              setAuditExportBusy(true);
                              try {
                                const batchNo = String(
                                  detailOverview?.BatchNumber ||
                                    detailOverview?.batchNumber ||
                                    "receipt",
                                )
                                  .replace(/[^\w.-]+/g, "_")
                                  .slice(0, 40);
                                await exportAuditLogToExcel(detailAudit, {
                                  fileName: `ePSF_Receipt_Audit_${batchNo}_${new Date()
                                    .toISOString()
                                    .slice(0, 10)}.xlsx`,
                                });
                              } catch (err) {
                                showAlert({
                                  title: "Export failed",
                                  message:
                                    err?.message ||
                                    "Could not export the audit log.",
                                  type: "error",
                                  buttonText: "OK",
                                });
                              } finally {
                                setAuditExportBusy(false);
                              }
                            }}
                          >
                            {auditExportBusy ? "Exporting…" : "Export Excel"}
                          </button>
                        </div>
                        <table className="epsf-receipt-detail__table">
                          <thead>
                            <tr>
                              <th>When</th>
                              <th>Who</th>
                              <th>Field</th>
                              <th>Reason</th>
                              <th>New value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailAudit.map((a) => (
                              <tr key={a.AuditId || a.auditId}>
                                <td>
                                  {utils.formatDateTimeDisplay(
                                    a.TimeCommitted || a.timeCommitted,
                                  )}
                                </td>
                                <td>{renderAuditActor(a)}</td>
                                <td>
                                  {formatAuditFieldLabelDisplay(
                                    a.ColumnName || a.columnName,
                                  )}
                                </td>
                                <td>
                                  {formatAuditReasonDisplay(
                                    a.Reason || a.reason,
                                  )}
                                </td>
                                <td>{renderAuditNewValue(a)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="epsf-receipt-detail-modal__empty">
                        No audit records for this receipt yet.
                      </p>
                    )
                  ) : (
                    <div className="epsf-receipt-detail__docs">
                      {canReceive ? (
                        <div className="epsf-receipt-detail__upload">
                          <div className="epsf-form-field">
                            <label className="form-label">Evidence type</label>
                            <select
                              className="form-control"
                              value={evidenceType}
                              onChange={(e) => setEvidenceType(e.target.value)}
                            >
                              {EVIDENCE_TYPE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="epsf-form-field epsf-receipt-detail__file-field">
                            <label className="form-label">File</label>
                            <input
                              type="file"
                              className="form-control"
                              onChange={(e) =>
                                setEvidenceFile(e.target.files?.[0] || null)
                              }
                            />
                            <div className="epsf-receipt-detail__file-name">
                              {evidenceFile
                                ? evidenceFile.name
                                : "No file selected"}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={!evidenceFile || detailBusy}
                            onClick={onUploadEvidence}
                          >
                            {detailBusy ? "Uploading…" : "Upload"}
                          </button>
                        </div>
                      ) : null}
                      {detailEvidence.length ? (
                        <ul className="epsf-receipt-detail__doc-list">
                          {detailEvidence.map((e) => {
                            const path = e.BlobPath || e.blobPath || "";
                            const href = path
                              ? `${API_CONFIG.blobBaseURL}${path.replace(
                                  /^\//,
                                  "",
                                )}`
                              : null;
                            const typeLabel =
                              EVIDENCE_TYPE_OPTIONS.find(
                                (o) =>
                                  o.value ===
                                  (e.EvidenceType || e.evidenceType),
                              )?.label ||
                              e.EvidenceType ||
                              e.evidenceType ||
                              "Evidence";
                            const fileName =
                              e.FileName || e.fileName || "Open file";
                            return (
                              <li
                                key={e.EvidenceId || e.evidenceId}
                                className="epsf-receipt-detail__doc-row"
                              >
                                <div className="epsf-receipt-detail__doc-meta">
                                  <span className="epsf-imp-pill epsf-imp-pill--status-default">
                                    {typeLabel}
                                  </span>
                                  {href ? (
                                    <a
                                      href={href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="epsf-receipt-detail__doc-link"
                                    >
                                      {fileName}
                                    </a>
                                  ) : (
                                    <span>{fileName}</span>
                                  )}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p className="epsf-receipt-detail-modal__empty">
                          No evidence documents yet.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="modal-footer py-2 epsf-receipt-detail-modal__footer">
                  {formError ? (
                    <p className="epsf-list-page__error mb-0 me-auto">
                      {formError}
                    </p>
                  ) : (
                    <span className="me-auto" />
                  )}
                  {canReleaseReceipt ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={releaseBusy || detailBusy}
                      onClick={onReleaseFromDetail}
                    >
                      {releaseBusy ? "Reviewing…" : "Review stock"}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setDetailOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
              </div>,
              document.body,
            )
          : null}

        <ReviewHeldStockModal
          open={reviewOpen && Boolean(reviewRow || detailOverview)}
          row={
            reviewRow ||
            (detailOverview
              ? { ...detailOverview, ImpTxnId: detailTxnId }
              : null)
          }
          studyId={ctx.studyId}
          siteId={ctx.siteId}
          busy={releaseBusy || detailBusy}
          onClose={() => {
            setReviewOpen(false);
            setReviewRow(null);
          }}
          onConfirm={onReviewConfirmedFromDetail}
        />
      </div>
      ) : null}

        {showModal
          ? createPortal(
              <div
                className="modal fade show d-block epsf-modal epsf-receipt-modal"
                tabIndex="-1"
                aria-modal="true"
              >
            <div className="modal-dialog modal-xl epsf-receipt-modal__dialog">
              <div className="modal-content epsf-receipt-modal__content">
                <form onSubmit={onSubmit} noValidate className="epsf-receipt-modal__form">
                  <div className="modal-header py-2">
                    <h5 className="modal-title mb-0">Add Receipt</h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={closeModal}
                      disabled={busy}
                    />
                  </div>
                  <div className="modal-body epsf-receipt-modal__body">
                    <div className="row g-3 epsf-receipt-modal__layout epsf-receipt-modal__layout--triple">
                      {/* Left: prepared batch search + batch info */}
                      <div className="col-12 col-lg-3 epsf-receipt-modal__pane">
                        <h3 className="epsf-receipt-section-label">
                          Prepared batches
                        </h3>
                        <section className="epsf-receipt-section epsf-receipt-section--add">
                          <div className="epsf-receipt-section__body epsf-receipt-batch-pane">
                            <div className="epsf-kit-map-batch-pane__search-row">
                              <input
                                id="epsf-receipt-batch-search"
                                type="search"
                                className="form-control form-control-sm epsf-kit-map-batch-pane__search"
                                disabled={busy || receivableBatchBusy}
                                value={batchListQuery}
                                placeholder="Search batch, expiry, status"
                                onChange={(e) =>
                                  setBatchListQuery(e.target.value)
                                }
                                aria-label="Search prepared batches"
                              />
                            </div>

                            {receivableBatchBusy ? (
                              <p className="epsf-kit-map-split__empty mb-0">
                                Loading batches…
                              </p>
                            ) : filteredReceivableBatches.length ? (
                              <div
                                className="epsf-kit-map-batch-pane__body"
                                role="listbox"
                                aria-label="Prepared batches"
                              >
                                {filteredReceivableBatches.map((opt) => {
                                  const isActive =
                                    selectedReceivableBatch?.value ===
                                    opt.value;
                                  return (
                                    <div
                                      key={opt.value}
                                      className={`epsf-kit-map-batch-row${
                                        isActive ? " is-selected" : ""
                                      }`}
                                    >
                                      <button
                                        type="button"
                                        role="option"
                                        aria-selected={isActive}
                                        className="epsf-kit-map-batch-row__btn"
                                        disabled={busy}
                                        title={opt.batchNumber}
                                        onClick={() =>
                                          onSelectReceivableBatch(
                                            isActive ? null : opt,
                                          )
                                        }
                                      >
                                        <span className="epsf-kit-map-batch-row__text">
                                          <span className="epsf-kit-map-batch-row__line1">
                                            <span className="epsf-kit-map-batch-row__name">
                                              {opt.batchNumber}
                                            </span>
                                          </span>
                                          <span className="epsf-kit-map-batch-row__meta">
                                            <span
                                              className={`epsf-kit-map-batch-row__chip epsf-kit-map-batch-row__chip--${
                                                opt.expiryTone || "none"
                                              }`}
                                              title={
                                                opt.expiryTone === "near"
                                                  ? `Expires within ${NEAR_EXPIRY_DAYS} days`
                                                  : opt.expiryTone === "expired"
                                                    ? "Batch expired"
                                                    : undefined
                                              }
                                            >
                                              {expiryChipLabel(opt.expiryIso)}
                                            </span>
                                            <span
                                              className={`epsf-kit-map-batch-row__chip epsf-kit-map-batch-row__chip--${
                                                opt.mappingTone || "none"
                                              }`}
                                              title={`${opt.mappedCount}/${opt.kitCount} Mapped`}
                                            >
                                              {opt.mappedCount}/{opt.kitCount}{" "}
                                              Mapped
                                            </span>
                                          </span>
                                        </span>
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="epsf-kit-map-split__empty mb-0">
                                {batchListQuery.trim()
                                  ? "No batches match this search."
                                  : "No IMP-mapped batches waiting to be received."}
                              </p>
                            )}
                            {fieldErrors.defaultKitBatchNumber ? (
                              <div className="epsf-form-field-error">
                                {fieldErrors.defaultKitBatchNumber}
                              </div>
                            ) : null}
                          </div>
                        </section>
                      </div>
                      {/* Middle: kit table (read-only from prepared batch) */}
                      <div className="col-12 col-lg-6 epsf-receipt-modal__pane">
                        <h3 className="epsf-receipt-section-label">
                          Kits <span className="mandatory">*</span>
                        </h3>
                        <section className="epsf-receipt-section epsf-receipt-section--kits">
                          <div className="epsf-receipt-section__body">
                            <div
                              className={`epsf-receipt-kit-readonly${
                                fieldErrors.kitNumbersText ? " is-invalid" : ""
                              }`}
                            >
                              <div className="epsf-receipt-kit-readonly__toolbar">
                                <span className="epsf-receipt-kit-readonly__count">
                                  {kitLines.length
                                    ? `${kitLines.length} kit${
                                        kitLines.length === 1 ? "" : "s"
                                      } from selected batch`
                                    : "No batch selected"}
                                </span>
                              </div>
                              <div className="epsf-receipt-kit-readonly__head">
                                <span>Kit number</span>
                                <span>IMP product</span>
                                <span>Expiry</span>
                              </div>
                              {kitLines.length ? (
                                <ul className="epsf-receipt-kit-readonly__list">
                                  {kitLines.map((line) => (
                                    <li
                                      key={line.kitNumber}
                                      className="epsf-receipt-kit-readonly__row"
                                    >
                                      <span
                                        className="epsf-receipt-kit-readonly__kit"
                                        title={line.kitNumber}
                                      >
                                        {line.kitNumber}
                                      </span>
                                      <span
                                        className="epsf-receipt-kit-readonly__imp"
                                        title={line.productLabel || "—"}
                                      >
                                        {line.productLabel || "—"}
                                      </span>
                                      <span className="epsf-receipt-kit-readonly__exp">
                                        {formatKitExpiryDisplay(line.expiryDate)}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="epsf-receipt-kit-readonly__empty">
                                  Select a prepared batch to see its mapped
                                  kits and IMP products.
                                </p>
                              )}
                            </div>
                            {fieldErrors.kitNumbersText ? (
                              <div className="epsf-form-field-error mt-1">
                                {fieldErrors.kitNumbersText}
                              </div>
                            ) : null}
                          </div>
                        </section>
                      </div>

                      {/* Right: receipt details */}
                      <div className="col-12 col-lg-3 epsf-receipt-modal__pane">
                        <h3 className="epsf-receipt-section-label">
                          Receipt details
                        </h3>
                        <section className="epsf-receipt-section epsf-receipt-section--details">
                          <div className="epsf-receipt-section__body">
                          <div className="epsf-form-field">
                            <label className="form-label">
                              Batch expiry <span className="mandatory">*</span>
                            </label>
                            <SegmentDatePicker
                              selected={form.defaultKitExpiryDate}
                              onChange={() => {}}
                              invalid={Boolean(fieldErrors.defaultKitExpiryDate)}
                              isClearable={false}
                              disabled
                              aria-label="Batch expiry"
                            />
                            {fieldErrors.defaultKitExpiryDate ? (
                              <div className="epsf-form-field-error">
                                {fieldErrors.defaultKitExpiryDate}
                              </div>
                            ) : null}
                          </div>
                          <div className="epsf-form-field">
                            <label className="form-label">
                              Received date <span className="mandatory">*</span>
                            </label>
                            <SegmentDatePicker
                              selected={form.receivedDate}
                              onChange={(date) => {
                                setForm((prev) => ({
                                  ...prev,
                                  receivedDate: date,
                                }));
                                setFieldErrors((prev) => ({
                                  ...prev,
                                  receivedDate: "",
                                }));
                              }}
                              maxDate={new Date()}
                              invalid={Boolean(fieldErrors.receivedDate)}
                              disabled={busy}
                              aria-label="Received date"
                            />
                            {fieldErrors.receivedDate ? (
                              <div className="epsf-form-field-error">
                                {fieldErrors.receivedDate}
                              </div>
                            ) : null}
                          </div>
                          <div className="epsf-form-field">
                            <label className="form-label">
                              Received time <span className="mandatory">*</span>
                            </label>
                            <SegmentTimePicker
                              value={form.receivedTime}
                              onChange={(val) => {
                                setForm((prev) => ({
                                  ...prev,
                                  receivedTime: val || "",
                                }));
                                setFieldErrors((prev) => ({
                                  ...prev,
                                  receivedTime: "",
                                }));
                              }}
                              invalid={Boolean(fieldErrors.receivedTime)}
                              disabled={busy}
                              aria-label="Received time"
                            />
                            {fieldErrors.receivedTime ? (
                              <div className="epsf-form-field-error">
                                {fieldErrors.receivedTime}
                              </div>
                            ) : null}
                          </div>
                          <div className="epsf-form-field">
                            <label className="form-label">
                              Storage location{" "}
                              <span className="mandatory">*</span>
                            </label>
                            <Dropdown
                              options={storageOptions}
                              className={
                                fieldErrors.storageUnitId ? "is-invalid" : ""
                              }
                              selected={findOption(
                                storageOptions,
                                form.storageUnitId,
                              )}
                              onSelect={(opt) => {
                                setForm((prev) => ({
                                  ...prev,
                                  storageUnitId: opt?.value || "",
                                  storageRackId: "",
                                  allowStorageOverride: false,
                                  storageOverrideReason: "",
                                  storageOverrideJustification: "",
                                }));
                                setFieldErrors((prev) => ({
                                  ...prev,
                                  storageUnitId: "",
                                  storageRackId: "",
                                }));
                              }}
                              disabled={busy}
                            />
                            {fieldErrors.storageUnitId ? (
                              <div className="epsf-form-field-error">
                                {fieldErrors.storageUnitId}
                              </div>
                            ) : null}
                          </div>
                          {unitHasRacks ? (
                            <div className="epsf-form-field">
                              <label className="form-label">
                                Storage rack <span className="mandatory">*</span>
                              </label>
                              <Dropdown
                                options={rackOptions}
                                className={
                                  fieldErrors.storageRackId ? "is-invalid" : ""
                                }
                                selected={findOption(
                                  rackOptions,
                                  form.storageRackId,
                                )}
                                onSelect={(opt) => {
                                  setForm((prev) => ({
                                    ...prev,
                                    storageRackId: opt?.value || "",
                                  }));
                                  setFieldErrors((prev) => ({
                                    ...prev,
                                    storageRackId: "",
                                  }));
                                }}
                                disabled={busy || !form.storageUnitId}
                              />
                              {fieldErrors.storageRackId ? (
                                <div className="epsf-form-field-error">
                                  {fieldErrors.storageRackId}
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          {storageIncompatible ? (
                            <div className="epsf-receipt-storage-compat is-bad mb-2">
                              <div>{storageWarningText}</div>
                              <label className="epsf-receipt-override-check">
                                <input
                                  type="checkbox"
                                  checked={Boolean(form.allowStorageOverride)}
                                  disabled={busy}
                                  onChange={(ev) => {
                                    const checked = ev.target.checked;
                                    setForm((prev) => ({
                                      ...prev,
                                      allowStorageOverride: checked,
                                      storageOverrideReason: checked
                                        ? prev.storageOverrideReason
                                        : "",
                                      storageOverrideJustification: checked
                                        ? prev.storageOverrideJustification
                                        : "",
                                    }));
                                  }}
                                />{" "}
                                Allow storage override
                              </label>
                              {form.allowStorageOverride ? (
                                <>
                                  <select
                                    className={`form-control form-control-sm mt-1${
                                      fieldErrors.storageOverrideReason
                                        ? " is-invalid"
                                        : ""
                                    }`}
                                    value={form.storageOverrideReason}
                                    disabled={busy}
                                    onChange={setField("storageOverrideReason")}
                                  >
                                    <option value="">Select reason</option>
                                    {(STORAGE_OVERRIDE_REASON_OPTIONS || []).map(
                                      (r) => (
                                        <option key={r.value} value={r.value}>
                                          {r.label}
                                        </option>
                                      ),
                                    )}
                                  </select>
                                  <textarea
                                    className={`form-control form-control-sm mt-1${
                                      fieldErrors.storageOverrideJustification
                                        ? " is-invalid"
                                        : ""
                                    }`}
                                    rows={2}
                                    maxLength={1000}
                                    value={form.storageOverrideJustification}
                                    disabled={busy}
                                    onChange={setField(
                                      "storageOverrideJustification",
                                    )}
                                    placeholder="Explain why this storage is used"
                                  />
                                </>
                              ) : null}
                            </div>
                          ) : null}
                          <div className="epsf-form-field">
                            <label className="form-label">
                              Condition on receipt{" "}
                              <span className="mandatory">*</span>
                            </label>
                            <Dropdown
                              options={CONDITION_OPTIONS}
                              className={
                                fieldErrors.conditionCode ? "is-invalid" : ""
                              }
                              selected={findOption(
                                CONDITION_OPTIONS,
                                form.conditionCode,
                              )}
                              onSelect={(opt) => {
                                setForm((prev) => ({
                                  ...prev,
                                  conditionCode: opt?.value || "",
                                  conditionDetails:
                                    opt?.value === "Acceptable"
                                      ? ""
                                      : prev.conditionDetails,
                                }));
                                setFieldErrors((prev) => ({
                                  ...prev,
                                  conditionCode: "",
                                  conditionDetails: "",
                                }));
                              }}
                              disabled={busy}
                            />
                            {fieldErrors.conditionCode ? (
                              <div className="epsf-form-field-error">
                                {fieldErrors.conditionCode}
                              </div>
                            ) : null}
                          </div>
                          {form.conditionCode &&
                          form.conditionCode !== "Acceptable" ? (
                            <div className="epsf-form-field">
                              <label className="form-label">
                                Issue details{" "}
                                <span className="mandatory">*</span>
                              </label>
                              <textarea
                                className={`form-control${
                                  fieldErrors.conditionDetails
                                    ? " is-invalid"
                                    : ""
                                }`}
                                rows={2}
                                maxLength={500}
                                value={form.conditionDetails}
                                onChange={setField("conditionDetails")}
                                placeholder="Describe the issue"
                                disabled={busy}
                              />
                              {fieldErrors.conditionDetails ? (
                                <div className="epsf-form-field-error">
                                  {fieldErrors.conditionDetails}
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          <div className="epsf-receipt-optional-fields">
                            <div className="epsf-form-field">
                              <label className="form-label">
                                Shipment / reference
                              </label>
                              <input
                                className={`form-control${
                                  fieldErrors.referenceNo ? " is-invalid" : ""
                                }`}
                                value={form.referenceNo}
                                onChange={setField("referenceNo")}
                                maxLength={100}
                                placeholder="e.g. SHIP-10045"
                                disabled
                                readOnly
                              />
                              {fieldErrors.referenceNo ? (
                                <div className="epsf-form-field-error">
                                  {fieldErrors.referenceNo}
                                </div>
                              ) : null}
                            </div>
                            <div className="epsf-form-field">
                              <label className="form-label">
                                Temperature ({tempUnitLabel})
                              </label>
                              <input
                                className={`form-control${
                                  fieldErrors.temperatureOnReceipt
                                    ? " is-invalid"
                                    : ""
                                }`}
                                value={form.temperatureOnReceipt}
                                onChange={setField("temperatureOnReceipt")}
                                placeholder="As written on shipment"
                                disabled
                                readOnly
                              />
                              {fieldErrors.temperatureOnReceipt ? (
                                <div className="epsf-form-field-error">
                                  {fieldErrors.temperatureOnReceipt}
                                </div>
                              ) : null}
                            </div>
                            <div className="epsf-form-field mb-0">
                              <label className="form-label">
                                Temperature when received ({tempUnitLabel})
                              </label>
                              <input
                                className={`form-control${
                                  fieldErrors.temperatureWhenReceived
                                    ? " is-invalid"
                                    : ""
                                }${
                                  tempStatus === "outside"
                                    ? " epsf-receipt-temp--outside"
                                    : ""
                                }`}
                                value={form.temperatureWhenReceived}
                                onChange={setField("temperatureWhenReceived")}
                                placeholder="e.g. 2.5"
                                disabled={busy}
                                inputMode="decimal"
                              />
                              {fieldErrors.temperatureWhenReceived ? (
                                <div className="epsf-form-field-error">
                                  {fieldErrors.temperatureWhenReceived}
                                </div>
                              ) : null}
                              {!fieldErrors.temperatureWhenReceived &&
                              tempStatus === "outside" &&
                              impTempMin != null &&
                              impTempMax != null ? (
                                <div className="epsf-form-field-hint epsf-form-field-hint--warn">
                                  Outside IMP storage range ({impTempMin}
                                  {tempUnitLabel} – {impTempMax}
                                  {tempUnitLabel}). Receipt may be held for
                                  review.
                                </div>
                              ) : null}
                              {!fieldErrors.temperatureWhenReceived &&
                              tempStatus === "within" &&
                              impTempMin != null &&
                              impTempMax != null ? (
                                <div className="epsf-form-field-hint">
                                  Within IMP storage range ({impTempMin}
                                  {tempUnitLabel} – {impTempMax}
                                  {tempUnitLabel}).
                                </div>
                              ) : null}
                            </div>
                          </div>
                          </div>
                        </section>
                      </div>

                    </div>
                  </div>
                  <div className="modal-footer py-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={closeModal}
                      disabled={busy}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={busy}
                    >
                      {busy ? "Saving..." : "Post receipt"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
              </div>,
              document.body,
            )
          : null}
      <EpsfFormFillModal
        open={Boolean(formFillModal?.formInstanceId)}
        formInstanceId={formFillModal?.formInstanceId || ""}
        workItem={formFillModal?.workItem || null}
        mode={formFillModal?.mode || "fill"}
        onClose={() => setFormFillModal(null)}
        onSubmitted={async () => {
          setFormFillModal(null);
          refreshFormDues();
          showAlert({
            title: "Form submitted",
            message:
              "Form submitted. If approval is required, status shows as Approval pending for the approver role.",
            type: "success",
          });
        }}
        onApproved={async (result) => {
          setFormFillModal(null);
          refreshFormDues();
          const wf = String(
            result?.rows?.[0]?.WorkflowStatus ||
              result?.WorkflowStatus ||
              "",
          ).toUpperCase();
          showAlert({
            title: wf === "CORRECTION_REQUIRED" ? "Form rejected" : "Form approved",
            message:
              wf === "CORRECTION_REQUIRED"
                ? "Form sent back for correction."
                : "Form approved and locked. Answers can no longer be changed.",
            type: wf === "CORRECTION_REQUIRED" ? "info" : "success",
          });
        }}
      />
      <InfoAlert {...alertState} onClose={hideAlert} />
    </EpsfAccessGate>
  );
}
